/*
Site Header
*/

function parseProductMarketMappings() {
  const mappings = window.Bero?.productMarketMappings || [];
  const usToUk = {};
  const ukToUs = {};

  mappings.forEach(({ us, uk }) => {
    if (!us || !uk) return;
    usToUk[us] = uk;
    if (!ukToUs[uk]) ukToUs[uk] = us;
  });

  return { usToUk, ukToUs };
}

let PRODUCT_MARKET_MAP_US_TO_UK = {};
let PRODUCT_MARKET_MAP_UK_TO_US = {};

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.megaMenuOpen = false;
    this.scrollActive = false;
    this.isSearchOpen = false;
    this.isMobileMenuOpen = false;

    this.isLocaleOpen = false;

    this.invertedHeader = this.hasAttribute("data-inverted");

    this.handleHeaderClick = this.handleHeaderClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleRivoHash = this.handleRivoHash.bind(this);
    this.handleLocaleOutsideClick = this.handleLocaleOutsideClick.bind(this);
    this.handleSearchOutsideClick = this.handleSearchOutsideClick.bind(this);

    this.addEventListener("click", this.handleHeaderClick);
    document.addEventListener("click", this.handleLocaleOutsideClick);
    document.addEventListener("click", this.handleSearchOutsideClick);
    document.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("hashchange", this.handleRivoHash);

    this.initMegaMenuHoverDelay();
    this.initLocaleToggle();
    this.initMobileLocaleSelect();

    this.handleViewportResize = this.handleViewportResize.bind(this);
    this.setViewportUnit();
    window.addEventListener("resize", this.handleViewportResize, {
      passive: true,
    });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.handleViewportResize, {
        passive: true,
      });
      window.visualViewport.addEventListener("scroll", this.handleViewportResize, {
        passive: true,
      });
    }

    this.handleScroll();
    if (this.invertedHeader && window.scrollY < 28) {
      this.applyInvertedHeaderStyle();
    } else {
      this.applyNormalizedHeaderStyle();
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleHeaderClick);
    document.removeEventListener("click", this.handleLocaleOutsideClick);
    document.removeEventListener("click", this.handleSearchOutsideClick);
    document.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("hashchange", this.handleRivoHash);

    window.removeEventListener("resize", this.handleViewportResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.handleViewportResize);
      window.visualViewport.removeEventListener("scroll", this.handleViewportResize);
    }

    this.unlockPageScroll();
  }

  getElements() {
    const root = this;

    const searchLayer = root.querySelector("[data-header-search]");
    const searchInput = searchLayer ? searchLayer.querySelector("[data-header-search-input]") : null;

    return {
      header: root.querySelector(".site-header"),
      headerContent: root.querySelector("[data-header-content]"),

      mobileMenu: root.querySelector("[data-mobile-menu]"),
      mobileToggles: root.querySelectorAll("[data-mobile-menu-toggle]"),

      megaRoot: root.querySelector("[data-mega-root]"),
      megaTrigger: root.querySelector("[data-mega-trigger]"),
      megaToggle: root.querySelector("[data-mega-toggle]"),
      megaDropdown: root.querySelector("[data-mega-dropdown]"),

      searchLayer,
      searchOpenButton: root.querySelector("[data-header-search-open]"),
      searchCloseButton: root.querySelector("[data-header-search-close]"),
      searchInput,

      localeToggle: root.querySelector("[data-header-locale-toggle]"),
      localeDropdown: root.querySelector("[data-header-locale-dropdown]"),
      localeForm: root.querySelector("[data-header-locale-dropdown] form"),
      mobileLocaleForm: root.querySelector("#MobileCountryForm"),
      mobileCountrySelect: root.querySelector("[data-mobile-country-select]"),
    };
  }

  initMobileLocaleSelect() {
    const { mobileLocaleForm, mobileCountrySelect } = this.getElements();
    if (!mobileLocaleForm || !mobileCountrySelect) return;

    const trigger = mobileLocaleForm.querySelector("[data-mobile-locale-trigger]");
    if (trigger) {
      trigger.addEventListener("click", (e) => {
        try {
          mobileCountrySelect.focus();
        } catch {}
      });
    }

    mobileCountrySelect.addEventListener("change", (e) => {
      const countryCode = e.target.value;
      if (!countryCode) return;

      this.submitMobileLocaleCountry(countryCode);
    });
  }

  submitMobileLocaleCountry(countryCode) {
    const { mobileLocaleForm } = this.getElements();
    if (!mobileLocaleForm) return;

    const input = mobileLocaleForm.querySelector('input[name="country_code"]');
    if (!input) return;

    input.value = countryCode;

    this.setReturnToForCountry(mobileLocaleForm, countryCode);

    mobileLocaleForm.submit();
  }

  handleScroll() {
    const above = window.scrollY >= 28;
    if (above === this.scrollActive) return;

    this.scrollActive = above;

    if (above) {
      this.setAttribute("data-active", "true");
      this.applyNormalizedHeaderStyle();
      return;
    }

    this.removeAttribute("data-active");

    if (this.invertedHeader && !this.megaMenuOpen && !this.isSearchOpen) {
      this.applyInvertedHeaderStyle();
    }
  }

  isRestrictedPath(pathname) {
    const restricted = ["/collections/merch"];

    return restricted.some((p) => pathname.includes(p));
  }

  getMappedProductPath(pathname, countryCode) {
    const match = pathname.match(/^\/products\/([^/?#]+)/);
    if (!match) return null;

    const handle = match[1];
    const isGB = (countryCode || "").toUpperCase() === "GB";

    if (isGB) {
      const ukHandle = PRODUCT_MARKET_MAP_US_TO_UK[handle];
      return ukHandle ? `/products/${ukHandle}` : null;
    } else {
      const usHandle = PRODUCT_MARKET_MAP_UK_TO_US[handle];
      return usHandle ? `/products/${usHandle}` : null;
    }
  }

  ensureReturnToInput(form) {
    if (!form) return null;

    let input = form.querySelector('input[name="return_to"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "return_to";
      form.appendChild(input);
    }
    return input;
  }

  setReturnToForCountry(form, countryCode) {
    if (!form) return;

    const input = this.ensureReturnToInput(form);
    if (!input) return;

    const pathname = window.location.pathname || "/";
    const search = window.location.search || "";
    const current = `${pathname}${search}`;

    const mappedPath = this.getMappedProductPath(pathname, countryCode);
    if (mappedPath) {
      input.value = mappedPath;
      return;
    }

    const isRestricted = this.isRestrictedPath(pathname);
    const isUS = (countryCode || "").toUpperCase() === "US";

    input.value = isRestricted && !isUS ? "/" : current;
  }

  applyNormalizedHeaderStyle() {
    const { header } = this.getElements();
    if (!header) return;

    header.style.color = "black";
    header.style.background = "white";
    header.style.borderBottomColor = "black";
  }

  applyInvertedHeaderStyle() {
    const { header } = this.getElements();
    if (!header) return;

    header.style.color = "white";
    header.style.background = "transparent";
    header.style.borderBottomColor = "transparent";
  }

  initMegaMenuHoverDelay() {
    const CLOSE_DELAY_MS = 120;
    const { megaRoot, megaToggle, megaDropdown } = this.getElements();
    if (!megaRoot || !megaDropdown) return;

    let closeTimer = null;

    const clearCloseTimer = () => {
      if (!closeTimer) return;
      clearTimeout(closeTimer);
      closeTimer = null;
    };

    const openMegaMenu = () => {
      this.megaMenuOpen = true;
      clearCloseTimer();
      megaRoot.setAttribute("data-open", "true");
      if (megaToggle) megaToggle.setAttribute("aria-expanded", "true");
      this.applyNormalizedHeaderStyle();
      this.closeSearch();
      this.closeLocaleDropdown();
    };

    const scheduleCloseMegaMenu = () => {
      clearCloseTimer();
      closeTimer = setTimeout(() => {
        this.megaMenuOpen = false;
        megaRoot.removeAttribute("data-open");
        if (megaToggle) megaToggle.setAttribute("aria-expanded", "false");

        if (this.invertedHeader && window.scrollY < 28 && !this.isSearchOpen) {
          this.applyInvertedHeaderStyle();
        }

        closeTimer = null;
      }, CLOSE_DELAY_MS);
    };

    // Exposed so Escape (handleKeyDown) can dismiss the mega menu and return
    // focus to its trigger.
    this.closeMegaMenu = () => {
      clearCloseTimer();
      this.megaMenuOpen = false;
      megaRoot.removeAttribute("data-open");
      if (megaToggle) megaToggle.setAttribute("aria-expanded", "false");
    };
    this._megaTrigger = megaToggle;

    // Touch taps are handled by the toggle's click; pointer hover is mouse/pen only.
    megaRoot.addEventListener("pointerenter", (e) => {
      if (e.pointerType !== "touch") openMegaMenu();
    });
    megaRoot.addEventListener("pointerleave", (e) => {
      if (e.pointerType !== "touch") scheduleCloseMegaMenu();
    });

    // The link stays a plain link; the caret button is the keyboard / screen
    // reader control. Focus alone doesn't open the menu, but focus moving
    // between the link, button and dropdown must not let it close.
    megaRoot.addEventListener("focusin", clearCloseTimer);
    megaRoot.addEventListener("focusout", scheduleCloseMegaMenu);

    if (megaToggle) {
      megaToggle.addEventListener("click", () => {
        if (this.megaMenuOpen) {
          this.closeMegaMenu();
        } else {
          openMegaMenu();
        }
      });
    }

    megaDropdown.addEventListener("focusin", openMegaMenu);
  }

  // Rivo renders its account slide-out outside the theme's markup. Any overlay
  // that inerts the page behind it also inerts that slide-out, which then
  // appears but cannot be focused, typed into or clicked. Close our overlays
  // before it opens. Bound to hashchange so inline #rivo links are covered too.
  handleRivoHash() {
    if (location.hash !== "#rivo") return;

    if (this.isSearchOpen) this.closeSearch();
    if (this.isMobileMenuOpen) this.closeMobileMenu();

    document.querySelectorAll("site-flyout").forEach((flyout) => {
      if (!Array.isArray(flyout.activePanels)) return;
      flyout.activePanels.slice().forEach((panel) => flyout.closePanel(panel));
    });

    if (window.BeroA11y) window.BeroA11y.releaseBackgroundInert();
  }

  handleHeaderClick(e) {
    const rivoBtn = e.target.closest("[data-rivo-login]");
    if (rivoBtn) {
      if (this.isMobileMenuOpen) this.closeMobileMenu();
      location.hash = "rivo";
      this.handleRivoHash();
      return;
    }

    if (this.handleMobileMenuToggleClick(e)) return;
    if (this.handleLocaleOptionClick(e)) return;
    if (this.handleSearchOpenClick(e)) return;
    if (this.handleSearchCloseClick(e)) return;
  }

  // The currency dropdown opens and closes on click only (Enter/Space on the
  // button fire click natively). It closes on a click outside, on Escape, or
  // when focus tabs out of the toggle + dropdown pair.
  initLocaleToggle() {
    const { localeToggle, localeDropdown } = this.getElements();
    if (!localeToggle || !localeDropdown) return;

    localeToggle.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.isLocaleOpen) {
        this.closeLocaleDropdown();
      } else {
        this.openLocaleDropdown();
      }
    });

    const handleFocusOut = (e) => {
      if (!this.isLocaleOpen) return;
      const next = e.relatedTarget;
      if (next && (localeToggle.contains(next) || localeDropdown.contains(next))) return;
      // relatedTarget is null when focus leaves the window or a click lands on
      // a non-focusable element; the outside-click handler covers the latter.
      if (!next) return;
      this.closeLocaleDropdown();
    };

    localeToggle.addEventListener("focusout", handleFocusOut);
    localeDropdown.addEventListener("focusout", handleFocusOut);
  }

  handleLocaleOutsideClick(e) {
    if (!this.isLocaleOpen) return;

    const { localeToggle, localeDropdown } = this.getElements();
    if (localeToggle && localeToggle.contains(e.target)) return;
    if (localeDropdown && localeDropdown.contains(e.target)) return;

    this.closeLocaleDropdown();
  }

  handleLocaleOptionClick(e) {
    if (!this.isLocaleOpen) return false;

    const option = e.target.closest("[data-value]");
    if (!option) return false;

    const { localeDropdown } = this.getElements();
    if (!localeDropdown || !localeDropdown.contains(option)) return false;

    e.preventDefault();

    const countryCode = option.dataset.value;
    if (!countryCode) return true;

    this.submitLocaleCountry(countryCode);
    return true;
  }

  openLocaleDropdown() {
    const { localeDropdown, localeToggle, megaRoot } = this.getElements();
    if (!localeDropdown) return;

    this.isLocaleOpen = true;

    if (this.closeMegaMenu) {
      this.closeMegaMenu();
    } else {
      if (megaRoot) megaRoot.removeAttribute("data-open");
      this.megaMenuOpen = false;
    }

    this.closeSearch();

    if (this._localeHideTimer) {
      clearTimeout(this._localeHideTimer);
      this._localeHideTimer = null;
    }
    if (this._localeShowTimer) {
      clearTimeout(this._localeShowTimer);
      this._localeShowTimer = null;
    }

    localeDropdown.hidden = false;
    localeDropdown.removeAttribute("data-open");

    if (localeToggle) localeToggle.setAttribute("aria-expanded", "true");

    this.applyNormalizedHeaderStyle();

    this._localeShowTimer = window.setTimeout(() => {
      if (!this.isLocaleOpen) return;
      localeDropdown.setAttribute("data-open", "true");
      this._localeShowTimer = null;
    }, 10);
  }

  closeLocaleDropdown() {
    const { localeDropdown, localeToggle } = this.getElements();
    if (!localeDropdown) return;

    this.isLocaleOpen = false;

    if (this._localeShowTimer) {
      clearTimeout(this._localeShowTimer);
      this._localeShowTimer = null;
    }
    if (this._localeHideTimer) {
      clearTimeout(this._localeHideTimer);
      this._localeHideTimer = null;
    }

    localeDropdown.removeAttribute("data-open");
    if (localeToggle) localeToggle.setAttribute("aria-expanded", "false");

    this._localeHideTimer = window.setTimeout(() => {
      if (this.isLocaleOpen) return;
      localeDropdown.hidden = true;
      this._localeHideTimer = null;
    }, 300);

    if (this.invertedHeader && window.scrollY < 28 && !this.megaMenuOpen && !this.isSearchOpen) {
      this.applyInvertedHeaderStyle();
    }
  }

  async submitLocaleCountry(countryCode) {
    const { localeForm, localeDropdown, localeToggle } = this.getElements();
    if (!localeForm) return;

    const input = localeForm.querySelector('input[name="country_code"]');
    if (!input) return;

    input.value = countryCode;

    this.setReturnToForCountry(localeForm, countryCode);

    await clearBundleItemsFromCart();
    localeForm.submit();

    this.isLocaleOpen = false;

    if (localeToggle) localeToggle.setAttribute("aria-expanded", "false");

    if (localeDropdown) {
      localeDropdown.removeAttribute("data-open");

      if (this._localeHideTimer) clearTimeout(this._localeHideTimer);
      this._localeHideTimer = window.setTimeout(() => {
        if (this.isLocaleOpen) return;
        localeDropdown.hidden = true;
        this._localeHideTimer = null;
      }, 300);
    }
  }

  handleMobileMenuToggleClick(e) {
    const btn = e.target.closest("[data-mobile-menu-toggle]");
    if (!btn) return false;

    e.preventDefault();
    this.toggleMobileMenu();
    return true;
  }

  handleSearchOpenClick(e) {
    const btn = e.target.closest("[data-header-search-open]");
    if (!btn) return false;

    e.preventDefault();
    this.toggleSearch(btn);
    return true;
  }

  handleSearchCloseClick(e) {
    const btn = e.target.closest("[data-header-search-close]");
    if (!btn) return false;

    e.preventDefault();
    this.closeSearch();
    return true;
  }

  // Listens on document: while the overlay is open the rest of the page is
  // inert, so a click there hit-tests through to body and never reaches the
  // header's own click handler.
  handleSearchOutsideClick(e) {
    if (!this.isSearchOpen) return;

    const { searchLayer } = this.getElements();
    if (!searchLayer || searchLayer.contains(e.target)) return;
    // The click that just opened the overlay bubbles up here too.
    if (e.target.closest && e.target.closest("[data-header-search-open]")) return;

    this.closeSearch();
  }

  handleKeyDown(e) {
    if (e.key !== "Escape") return;

    if (this.isSearchOpen) this.closeSearch();
    if (this.isLocaleOpen) {
      const { localeToggle, localeDropdown } = this.getElements();
      const focusWasInside = localeDropdown && localeDropdown.contains(document.activeElement);
      this.closeLocaleDropdown();
      if (focusWasInside && localeToggle) localeToggle.focus();
    }
    if (this.isMobileMenuOpen) this.closeMobileMenu();
    if (this.megaMenuOpen && this.closeMegaMenu) {
      this.closeMegaMenu();
      if (this._megaTrigger) this._megaTrigger.focus();
    }
  }

  lockPageScroll() {
    this._scrollLockActive = true;
    document.body.style.overflow = "hidden";
    // Mirrors the flyout's `flyout-open`: the open menu is a modal that inerts
    // the page behind it, so the Pandectes banner (z-index: 2147483647) is left
    // visible but dead on top of the menu. The class drops it below the header.
    document.body.classList.add("mobile-menu-open");
  }

  unlockPageScroll() {
    this._scrollLockActive = false;
    document.body.style.overflow = "auto";
    document.body.classList.remove("mobile-menu-open");
  }

  toggleMobileMenu() {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  setViewportUnit() {
    const vv = window.visualViewport;
    const h = vv && vv.height ? vv.height : window.innerHeight;

    const vvh = h * 0.01;
    document.documentElement.style.setProperty("--vvh", `${vvh}px`);
  }

  handleViewportResize() {
    this.setViewportUnit();

    if (this.isMobileMenuOpen) {
      this.applyMobileMenuAnnouncementOffset();
    }
  }

  applyMobileMenuAnnouncementOffset() {
    const { mobileMenu } = this.getElements();
    if (!mobileMenu) return;

    const ANNOUNCEMENT_HEIGHT = 28;
    const scrolled = Math.max(0, Math.min(ANNOUNCEMENT_HEIGHT, window.scrollY || 0));
    const remaining = ANNOUNCEMENT_HEIGHT - scrolled;

    mobileMenu.style.height = `calc((var(--vvh, 1vh) * 100) - 56px - ${remaining}px)`;
  }

  openMobileMenu() {
    this.applyNormalizedHeaderStyle();

    const { mobileMenu, mobileToggles } = this.getElements();
    if (!mobileMenu || document.readyState !== "complete") return;

    this.isMobileMenuOpen = true;

    this.setViewportUnit();
    this.applyMobileMenuAnnouncementOffset();

    this.lockPageScroll();

    // Remember the toggle so focus can return to it on close.
    this._mobileMenuTrigger =
      (window.BeroA11y && window.BeroA11y.saveFocus()) ||
      (mobileToggles && mobileToggles[0]) ||
      null;

    mobileMenu.setAttribute("data-open", "true");
    mobileToggles.forEach((btn) => {
      btn.setAttribute("data-open", "true");
      btn.setAttribute("aria-expanded", "true");
    });

    // Treat the full-screen drawer as a modal: inert the page behind it
    // (keeping the header chrome live so the toggle stays operable) and trap
    // focus inside the menu. Escape closes and restores focus.
    if (window.BeroA11y) {
      window.BeroA11y.setBackgroundInert(this);
      // Focus the drawer itself (not its search input) so the on-screen
      // keyboard doesn't pop on touch; keyboard users Tab from here.
      window.BeroA11y.trapFocus(mobileMenu, mobileMenu);
    }
  }

  closeMobileMenu() {
    if (this.invertedHeader && window.scrollY < 28 && !this.megaMenuOpen && !this.isSearchOpen) {
      this.applyInvertedHeaderStyle();
    }

    const { mobileMenu, mobileToggles } = this.getElements();
    if (!mobileMenu) return;

    this.isMobileMenuOpen = false;

    mobileMenu.removeAttribute("data-open");
    mobileToggles.forEach((btn) => {
      btn.removeAttribute("data-open");
      btn.setAttribute("aria-expanded", "false");
    });

    mobileMenu.style.height = "";

    this.unlockPageScroll();

    if (window.BeroA11y) {
      window.BeroA11y.releaseFocus(mobileMenu);
      window.BeroA11y.releaseBackgroundInert();
    }

    const returnTo =
      this._mobileMenuTrigger && document.contains(this._mobileMenuTrigger)
        ? this._mobileMenuTrigger
        : mobileToggles && mobileToggles[0];
    if (window.BeroA11y) window.BeroA11y.restoreFocus(returnTo);
    else if (returnTo) returnTo.focus();
    this._mobileMenuTrigger = null;
  }

  toggleSearch(trigger) {
    if (this.isSearchOpen) {
      this.closeSearch();
    } else {
      this.openSearch(trigger);
    }
  }

  setSearchExpanded(searchLayer, open) {
    if (!searchLayer.id) return;
    this.querySelectorAll(`[data-header-search-open][aria-controls="${searchLayer.id}"]`).forEach((btn) => {
      btn.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  openSearch(trigger) {
    const { searchLayer, megaRoot, searchInput } = this.getElements();
    if (!searchLayer) return;

    // Not rendered at this viewport (the LP header's overlay is display:none
    // below 768px): opening would inert the page behind an invisible dialog.
    if (window.getComputedStyle(searchLayer).display === "none") return;

    this.isSearchOpen = true;
    this._searchTrigger = trigger || (window.BeroA11y && window.BeroA11y.saveFocus()) || null;

    if (this.closeMegaMenu) {
      this.closeMegaMenu();
    } else {
      if (megaRoot) megaRoot.removeAttribute("data-open");
      this.megaMenuOpen = false;
    }

    this.closeLocaleDropdown();

    searchLayer.classList.remove("hidden");
    searchLayer.setAttribute("data-open", "true");
    searchLayer.style.opacity = "1";
    searchLayer.style.pointerEvents = "auto";

    // Flush the visibility change before focusing: the layer is
    // visibility:hidden while closed, and focus() is a no-op on a still-hidden
    // element (requestAnimationFrame runs before the style recalc).
    void searchLayer.offsetWidth;

    this.applyNormalizedHeaderStyle();
    this.setSearchExpanded(searchLayer, true);

    // The overlay is a modal dialog: inert everything outside it (the rest of
    // the header included) so neither Tab nor a screen reader's virtual cursor
    // can reach the page behind, and trap Tab inside. Escape, the close
    // button or a click outside dismisses it.
    if (window.BeroA11y) {
      window.BeroA11y.setBackgroundInert(searchLayer);
      window.BeroA11y.trapFocus(searchLayer, searchInput || searchLayer);
    } else if (searchInput) {
      requestAnimationFrame(() => {
        try {
          searchInput.focus();
        } catch {}
      });
    }
  }

  closeSearch() {
    const { searchLayer, searchOpenButton } = this.getElements();
    // Called unconditionally from the mega menu / locale openers: bail when
    // already closed so we never release another overlay's background inert.
    if (!searchLayer || !this.isSearchOpen) return;

    this.isSearchOpen = false;

    // If focus is still inside the search overlay (keyboard close / Escape),
    // return it to the button that opened it so focus is never lost.
    const focusInsideSearch = searchLayer.contains(document.activeElement);

    searchLayer.removeAttribute("data-open");
    searchLayer.style.opacity = "0";
    searchLayer.style.pointerEvents = "none";

    window.setTimeout(() => {
      if (this.isSearchOpen) return;
      searchLayer.classList.add("hidden");
    }, 300);

    this.setSearchExpanded(searchLayer, false);

    // Release before restoring focus: the trigger is inert until then.
    if (window.BeroA11y) {
      window.BeroA11y.releaseFocus(searchLayer);
      window.BeroA11y.releaseBackgroundInert();
    }

    const returnTo =
      this._searchTrigger && document.contains(this._searchTrigger) ? this._searchTrigger : searchOpenButton;
    this._searchTrigger = null;

    if (focusInsideSearch && returnTo) {
      try { returnTo.focus(); } catch {}
    }

    if (this.invertedHeader && window.scrollY < 28 && !this.megaMenuOpen) {
      this.applyInvertedHeaderStyle();
    }
  }
}

customElements.define("site-header", SiteHeader);

/*
Site Flyout
*/

if (!customElements.get("site-flyout")) {
  class SiteFlyout extends HTMLElement {
    connectedCallback() {
      this.activePanels = [];
      this._returnFocusStack = [];
      this.setupKeyboardListeners();
    }

    setupKeyboardListeners() {
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.activePanels.length > 0) {
          const lastPanel = this.activePanels[this.activePanels.length - 1];
          this.closePanel(lastPanel);
        }
      });
    }

    openPanel(panel = "cart") {
      const panelEl = this.findPanel(panel);
      if (!panelEl) return;

      // No-op if this panel is already open (e.g. add-to-cart while cart open).
      if (this.activePanels.includes(panel)) return;

      // Remember where focus was so we can restore it when the panel closes.
      const trigger = window.BeroA11y ? window.BeroA11y.saveFocus() : document.activeElement;
      this._returnFocusStack.push(trigger);

      panelEl.classList.add("active");
      panelEl.setAttribute("aria-hidden", "false");
      const openHost = panelEl.closest("flyout-panel");
      if (openHost) openHost.setAttribute("aria-hidden", "false");
      this.activePanels.push(panel);

      this.handleOpenOrClose();

      // Make the rest of the page inert the first time a panel opens.
      if (window.BeroA11y && this.activePanels.length === 1) {
        window.BeroA11y.setBackgroundInert(this);
      }

      const target = this.getPanelFocusTarget(panelEl);
      if (window.BeroA11y) {
        window.BeroA11y.trapFocus(panelEl, target);
      } else if (target && typeof target.focus === "function") {
        target.focus();
      }
    }

    getPanelFocusTarget(panelEl) {
      const closeButton = panelEl.querySelector("close-panel button");
      if (closeButton) return closeButton;

      const heading = panelEl.querySelector("h1, h2, [role='heading']");
      if (heading) {
        if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
        return heading;
      }

      if (window.BeroA11y) {
        const first = window.BeroA11y.getFocusableElements(panelEl)[0];
        if (first) return first;
      }

      if (!panelEl.hasAttribute("tabindex")) panelEl.setAttribute("tabindex", "-1");
      return panelEl;
    }

    closePanel(panel) {
      const panelEl = this.findPanel(panel);
      if (!panelEl) return;
      if (!this.activePanels.includes(panel)) return;

      panelEl.classList.remove("active");
      panelEl.setAttribute("aria-hidden", "true");
      const closeHost = panelEl.closest("flyout-panel");
      if (closeHost) closeHost.setAttribute("aria-hidden", "true");
      this.activePanels = this.activePanels.filter((p) => p !== panel);

      const returnTarget = this._returnFocusStack.pop();

      this.handleOpenOrClose();

      if (window.BeroA11y) {
        window.BeroA11y.releaseFocus(panelEl);
        if (this.activePanels.length === 0) {
          window.BeroA11y.releaseBackgroundInert();
        }
      }

      // Return focus to whatever opened the panel; fall back to its trigger.
      // The opener may have been re-rendered while the panel was open (e.g. the PDP
      // swaps #pdp-add-cart after add-to-cart), so look for its replacement by id.
      const fallback = document.querySelector(`[data-panel="${panel}"]`);
      let focusTo = fallback;
      if (returnTarget && document.contains(returnTarget)) {
        focusTo = returnTarget;
      } else if (returnTarget?.id) {
        const replacement = document.getElementById(returnTarget.id);
        if (replacement && !replacement.disabled) focusTo = replacement;
      }
      if (window.BeroA11y) {
        window.BeroA11y.restoreFocus(focusTo);
      } else if (focusTo && typeof focusTo.focus === "function") {
        focusTo.focus();
      }
    }

    closeAllPanels() {
      // Close in reverse (LIFO) so stacked focus targets restore in order.
      [...this.activePanels].reverse().forEach((panel) => {
        this.closePanel(panel);
      });
    }

    findPanel(panel) {
      return this.querySelector(`flyout-panel[data-name="${panel}"] .flyout-panel`);
    }

    handleOpenOrClose() {
      const veil = this.querySelector(".flyout-veil");
      const closePanels = this.querySelector("close-panels");

      if (this.activePanels.length > 0) {
        if (this._prevBodyOverflow === undefined) {
          this._prevBodyOverflow = document.body.style.overflow;
        }
        document.body.style.overflow = "hidden";
        document.body.classList.add("flyout-open");
        if (veil) veil.classList.add("active");
        if (closePanels) closePanels.setAttribute("aria-hidden", "false");
      } else {
        document.body.style.overflow = this._prevBodyOverflow || "";
        this._prevBodyOverflow = undefined;
        document.body.classList.remove("flyout-open");
        if (veil) veil.classList.remove("active");
        if (closePanels) closePanels.setAttribute("aria-hidden", "true");
      }
    }
  }

  customElements.define("site-flyout", SiteFlyout);
}

/*
Open Panel
*/

class OpenPanel extends HTMLElement {
  constructor() {
    super();
    this.panel = this.dataset.panel || "cart";
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.error("ToggleFlyout needs a child button");
    }

    this.addEventListener("click", (e) => {
      const flyout = document.querySelector("site-flyout");

      if (flyout) {
        flyout.openPanel(this.panel);
      }
    });
  }
}

customElements.define("open-panel", OpenPanel);

/*
Close Panel
*/

class ClosePanel extends HTMLElement {
  constructor() {
    super();
    this.panel = this.dataset.panel || "cart";
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.error("ToggleFlyout needs a child button");
    }

    this.addEventListener("click", (e) => {
      const flyout = document.querySelector("site-flyout");

      if (flyout) {
        flyout.closePanel(this.panel);
      }
    });
  }
}

customElements.define("close-panel", ClosePanel);

/*
Close Panels
*/

class ClosePanels extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.error("ClosePanels needs a child button");
    }

    this.addEventListener("click", (e) => {
      const flyout = document.querySelector("site-flyout");
      flyout.closeAllPanels();
    });
  }
}

customElements.define("close-panels", ClosePanels);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }
    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");
      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    } else {
      pagerButtons[0].classList.add("active");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error("<carousel-pager> needs to be a child of <site-carousel>");
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.config = null;
  }

  connectedCallback() {
    this.config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: false };

    const prevSelector = this.dataset.prevSelector || ".carousel-prev";
    const nextSelector = this.dataset.nextSelector || ".carousel-next";

    const prevEl = this.querySelector(prevSelector);
    const nextEl = this.querySelector(nextSelector);

    const scrollbarEl = this.querySelector(".carousel-scrollbar");
    const paginationEl = this.querySelector(".carousel-pager");

    const updatePaginationTheme = (swiper) => {
      const activeSlide = swiper?.slides?.[swiper.activeIndex];
      const shouldInvert = activeSlide?.dataset?.invertPagination === "true";
      this.classList.toggle("invert-pagination", shouldInvert);
    };

    const updateBarVisibility = (swiper) => {
      const bar = this.querySelector(".site-carousel-bar");
      if (bar) bar.style.display = swiper.isLocked ? "none" : "";
    };

    const swiperOptions = {
      ...this.config,
      watchOverflow: true,
      on: {
        init: (swiper) => {
          this.classList.remove("opacity-0");
          updatePaginationTheme(swiper);
          updateBarVisibility(swiper);
          this.syncSlideFocusability();
        },
        slideChange: (swiper) => updatePaginationTheme(swiper),
        resize: (swiper) => {
          updateBarVisibility(swiper);
          this.syncSlideFocusability();
        },
        transitionEnd: () => this.syncSlideFocusability(),
        slideChangeTransitionEnd: () => this.syncSlideFocusability(),
        observerUpdate: () => this.syncSlideFocusability(),
      },
    };

    if (prevEl || nextEl) {
      swiperOptions.navigation = { nextEl, prevEl };
    }

    if (scrollbarEl) {
      swiperOptions.scrollbar = { el: scrollbarEl, draggable: false };
    }

    this.isScrollbar = this.dataset.isScrollbar == "true";

    if (paginationEl && !this.isScrollbar) {
      swiperOptions.pagination = {
        el: paginationEl,
        bulletClass: "carousel-bullet",
        bulletActiveClass: "active",
        clickable: true,
      };
    }

    if (scrollbarEl && this.isScrollbar) {
      swiperOptions.scrollbar = {
        el: scrollbarEl,
      };
    }

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, swiperOptions);
  }

  syncSlideFocusability() {
    if (!window.BeroA11y) return;

    const viewport = this.querySelector(".swiper");
    if (!viewport) return;

    const slides = viewport.querySelectorAll(
      ":scope > .swiper-wrapper > .swiper-slide"
    );
    if (!slides.length) return;

    // Fade carousels (PDP gallery) stack every slide in place, so "offscreen" never
    // applies: only the active slide should be exposed to screen readers.
    if (this.config?.effect === "fade") {
      slides.forEach((slide) => {
        const active = slide.classList.contains("swiper-slide-active");
        if (active) slide.removeAttribute("aria-hidden");
        else slide.setAttribute("aria-hidden", "true");
        window.BeroA11y.setSubtreeFocusable(slide, active);
      });
      return;
    }

    const bounds = viewport.getBoundingClientRect();
    if (bounds.width === 0) return;

    slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const offscreen =
        rect.right <= bounds.left + 1 || rect.left >= bounds.right - 1;
      window.BeroA11y.setSubtreeFocusable(slide, !offscreen);
    });
  }

  previous() {
    if (this.swiper) this.swiper.slidePrev();
  }

  next() {
    if (this.swiper) this.swiper.slideNext();
  }

  slideTo(i) {
    if (this.swiper) this.swiper.slideTo(i);
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Cart Item
*/

class CartItem extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.idx = this.dataset.idx;

    this.selectedVariant = JSON.parse(this.dataset.selectedVariant);

    this.removeEl = this.querySelector(".remove");
    this.quantityInputEl = this.querySelector("number-input input");
    this.quantityControlEl = this.querySelector("number-input");
    this.upgradeButtonEl = this.querySelector(".upgrade-to-sub-js");
    this.sellingPlanSelectEl = this.querySelector(".selling-plan-select");
    this.totals = this.querySelector(".cart-item__totals");
    this.siteCart = this.closest("site-cart");

    this.addRemoveEvent();
    this.addQuantityChangeEvent();
    this.addSubscriptionHandlers();
  }

  showLoader() {
    this.totals.firstElementChild.classList.add("opacity-0");
    this.totals.lastElementChild.classList.remove("hidden");
  }

  hideLoader() {
    this.totals.firstElementChild.classList.remove("opacity-0");
    this.totals.lastElementChild.classList.add("hidden");
  }

  removeItem(idx = this.idx) {
    this.showLoader();
    this.siteCart.updateQuantity(idx, 0);
  }

  addRemoveEvent() {
    if (this.removeEl) {
      this.removeEl.addEventListener("click", () => {
        this.removeItem();
      });
    }
  }

  addQuantityChangeEvent() {
    if (!this.quantityInputEl) return;

    this.quantityInputEl.addEventListener("change", async () => {
      const newQty = +this.quantityInputEl.value;
      const cartItem = this.siteCart.cartData?.items?.[this.idx - 1];

      // GWP items: keep the free line at qty 1, add extras as a separate paid line
      if (cartItem?.properties?._gwp && newQty > 1) {
        this.showLoader();
        // Reset the input back to 1 visually
        this.quantityInputEl.value = 1;
        // Add a separate paid line (no _gwp property)
        await this.siteCart.addVariantToCart(cartItem.variant_id, newQty - 1, undefined, undefined, {
          openCart: false,
        });
        return;
      }

      this.showLoader();
      this.siteCart.updateQuantity(this.idx, newQty);
    });
  }

  addSubscriptionHandlers() {
    if (this.upgradeButtonEl && this.sellingPlanSelectEl) {
      this.sellingPlanSelectEl.addEventListener("change", async () => {
        const planId = this.sellingPlanSelectEl.value;
        if (!planId) return;

        const qty = this.quantityInputEl ? parseInt(this.quantityInputEl.value, 10) : 1;
        await this.convertLineToSubscription({ planId, quantity: qty });
      });
    }
  }

  async convertLineToSubscription({ planId, quantity }) {
    this.showLoader();

    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ line: this.idx, quantity: 0 }),
      });

      await this.siteCart.addVariantToCart(this.selectedVariant.id, quantity, parseInt(planId, 10), undefined, {
        openCart: !this.siteCart._isCartPage,
      });

      this.siteCart.handleRewards();
    } catch (err) {
      console.error("Subscription switch failed:", err);
    } finally {
      this.hideLoader();
    }
  }
}

customElements.define("cart-item", CartItem);

/*
Site Cart
*/

const formatMoneyNoTrailingZeros = (cents) => {
  const currencySymbol = window?.Bero?.storeCurrencySymbol || "$";
  const v = (Number(cents) || 0) / 100;
  const s = v.toFixed(2);
  const out = s.endsWith(".00") ? s.slice(0, -3) : s;
  return `${currencySymbol}${out}`;
};

const applyPricingState = () => {
  const isMember = !!window?.Bero?.hasBeromasterInCart || !!window?.Bero?.isRivoSubscriber;

  const toCents = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  document.querySelectorAll("[data-price-wrap]").forEach((wrap) => {
    const compareEl = wrap.querySelector("[data-price-compare]");
    const priceEl = wrap.querySelector("[data-price-active]");
    if (!priceEl) return;

    const hasShopifyLinePricing =
      wrap.dataset.shopifyFinalLine !== undefined || wrap.dataset.shopifyOriginalLine !== undefined;

    if (hasShopifyLinePricing) {
      const originalLine = toCents(wrap.dataset.shopifyOriginalLine);
      const finalLine = toCents(wrap.dataset.shopifyFinalLine);

      const hasVip = wrap.dataset.hasVip === "true";
      const vipLine = toCents(wrap.dataset.vipLine);
      const isUkByob = wrap.dataset.isUkByob === "true";

      const activeCents = hasVip ? vipLine : finalLine;

      let compareCents = 0;
      if (isUkByob) {
        // UK BYOB: never show strikethrough — Skio box discount is hidden
        compareCents = 0;
      } else if (hasVip) {
        compareCents = originalLine > 0 ? originalLine : finalLine;
      } else if (originalLine > finalLine) {
        compareCents = originalLine;
      }

      priceEl.textContent = formatMoneyNoTrailingZeros(activeCents);

      if (compareEl) {
        if (compareCents > activeCents) {
          compareEl.classList.remove("hidden");
          compareEl.classList.add("line-through", "opacity-50");
          compareEl.textContent = formatMoneyNoTrailingZeros(compareCents);
        } else {
          compareEl.classList.add("hidden");
          compareEl.textContent = "";
        }
      }

      return;
    }

    const isBeromasterProduct = priceEl.dataset.isBeromaster === "true" || compareEl?.dataset.isBeromaster === "true";

    const useMember = isMember || isBeromasterProduct;

    const nextPriceRaw = useMember ? priceEl.dataset.priceMember : priceEl.dataset.priceNonmember;

    if (nextPriceRaw === undefined || nextPriceRaw === null || nextPriceRaw === "") {
      return;
    }

    const nextPrice = toCents(nextPriceRaw);
    priceEl.textContent = formatMoneyNoTrailingZeros(nextPrice);

    if (!compareEl) return;

    const nextCompareRaw = useMember ? compareEl.dataset.compareMember : compareEl.dataset.compareNonmember;

    if (nextCompareRaw === undefined || nextCompareRaw === null || nextCompareRaw === "") {
      compareEl.classList.add("hidden");
      compareEl.textContent = "";
      return;
    }

    const nextCompare = toCents(nextCompareRaw);
    const shouldShowCompare = useMember ? nextCompare > nextPrice : nextCompare > 0;

    if (shouldShowCompare) {
      compareEl.classList.remove("hidden");
      compareEl.classList.add("line-through", "opacity-50");
      compareEl.textContent = formatMoneyNoTrailingZeros(nextCompare);
    } else {
      compareEl.classList.add("hidden");
      compareEl.textContent = "";
    }
  });

  document.querySelectorAll("product-item").forEach((item) => item.syncAtcAriaLabel?.());
};

const CART_NOTICES_KEY = "bero:cart-notices";

class SiteCart extends HTMLElement {
  static _primaryInstance = null;

  constructor() {
    super();
  }

  // 1) In connectedCallback: do NOT call reloadCart on cart page init
  connectedCallback() {
    // Singleton: only the first <site-cart> instance fully initialises.
    // On /cart the cart-page instance appears first in the DOM (content_for_layout
    // precedes site-flyout in the layout), so it becomes primary. On every other
    // page only the flyout instance exists.
    if (SiteCart._primaryInstance && SiteCart._primaryInstance !== this) {
      this._isPassive = true;
      return;
    }
    SiteCart._primaryInstance = this;

    window.Bero = window.Bero || {};
    window.Bero._triggerGwp = () => this._addMatchingGwpGifts();

    this.flyout = document.querySelector("site-flyout");
    this.gwpVariantId = this.dataset.gwpVariantId;

    this._hasHydrated = false;
    this._isCartPage =
      window.location.pathname === "/cart" ||
      document.body.classList.contains("template-cart") ||
      document.documentElement.classList.contains("template-cart");

    this._cartNotices = this._readCartNotices();
    this._cleanedLineKeys = new Set();

    Promise.all([
      this.validateBeromaster(),
      this._isCartPage ? this.fetchCart() : this.reloadCart({ reason: "init" }),
    ]).then(async () => {
      this.handleRewards({ reason: "init" });

      // Listen for remove-bundle clicks on ALL <site-cart> containers
      document.querySelectorAll("site-cart").forEach((sc) => {
        sc.addEventListener("click", (e) => {
          const btn = e.target.closest("button");
          if (btn && btn.matches(".remove-bundle")) {
            this.removeBYOB();
          }
        });
      });

      this.currencySymbol = window?.Bero?.storeCurrencySymbol || "$";

      await this.loadCart();

      this._hasHydrated = true;

      this._watchCartFlyout();
      this._renderCartNotices();
      await this.cleanUpHiddenSubscriptions();
    });
  }

  async openCart() {
    this.reloadCart();
    this.handleRewards();
    this.flyout.openPanel("cart");
  }

  async validateBeromaster() {
    if (window.Bero.isRivoSubscriber && window.Bero.hasBeromasterInCart) {
      await this.removeItem(window.Bero.beromasterVariantId, true);
    }
  }

  getCartSections() {
    return [
      { section: "main-cart-items", selector: "[data-cart-section='items']" },
      { section: "main-cart-footer", selector: "[data-cart-section='footer']" },
      { section: "main-cart-header", selector: "[data-cart-section='header']" },
    ];
  }

  getSectionInnerHTML(html, selector) {
    const el = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    return el ? el.innerHTML : "";
  }

  renderCartSections(sections) {
    // Capture the focused control BEFORE the swap so we can restore it after
    // (focus is still on the +/- or remove button at this point).
    const focusDesc = this._captureCartFocus();

    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      if (!activeSection) return;

      const selectors = activeSection.selectors ?? [activeSection.selector];

      selectors.forEach((selector) => {
        const html = this.getSectionInnerHTML(sections[sectionKey], selector);
        if (!html) return;

        // Update ALL matching elements (cart page + flyout)
        document.querySelectorAll(selector).forEach((el) => {
          el.innerHTML = html;
        });
      });
    });

    this._restoreCartFocus(focusDesc);
    this._renderCartNotices();
    this.cleanUpHiddenSubscriptions();
  }

  // Records which cart control currently has focus (line + control), so a
  // re-render doesn't dump the user on the Close button or <body>.
  _captureCartFocus() {
    const ae = document.activeElement;
    if (!ae || ae === document.body) return null;
    if (!ae.closest("[data-cart-section]")) return null;

    const item = ae.closest("cart-item[data-idx]");
    return {
      idx: item ? item.getAttribute("data-idx") : null,
      name: ae.getAttribute("name") || null, // "increment" | "decrement"
      cls: ae.classList.contains("remove")
        ? "remove"
        : ae.classList.contains("remove-bundle")
        ? "remove-bundle"
        : null,
      flyoutOpen: !!(this.flyout && this.flyout.activePanels && this.flyout.activePanels.includes("cart")),
    };
  }

  // Restores focus to the equivalent control after a re-render. Falls back to
  // the dialog (Close button) only when the original control is truly gone
  // (e.g. the line item was removed).
  _restoreCartFocus(desc) {
    if (desc) {
      const root =
        desc.flyoutOpen && this.flyout ? this.flyout.findPanel("cart") : document;
      let sel = "";
      if (desc.idx) sel += `cart-item[data-idx="${desc.idx}"] `;
      if (desc.name) sel += `[name="${desc.name}"]`;
      else if (desc.cls) sel += `.${desc.cls}`;
      const target = sel.trim() && root ? root.querySelector(sel.trim()) : null;
      if (target) {
        try {
          target.focus();
          return;
        } catch (e) {}
      }
    }
    this._restoreCartDialogFocus();
  }

  // Swapping cart section innerHTML can destroy the focused element (e.g. the
  // close button right after add-to-cart opened the flyout). If that drops
  // focus to <body>, pull it back into the open cart dialog. No-op on the cart
  // page and when focus is already somewhere meaningful inside the dialog.
  _restoreCartDialogFocus() {
    if (!this.flyout || !this.flyout.activePanels) return;
    if (!this.flyout.activePanels.includes("cart")) return;

    const panelEl = this.flyout.findPanel("cart");
    if (!panelEl) return;

    const active = document.activeElement;
    const focusLost = !active || active === document.body || !panelEl.contains(active);
    if (!focusLost) return;

    const target = panelEl.querySelector("close-panel button") || panelEl;
    if (target === panelEl && !panelEl.hasAttribute("tabindex")) {
      panelEl.setAttribute("tabindex", "-1");
    }
    try { target.focus(); } catch (e) {}
  }

  // Shopify keeps cart lines when the country changes, so a subscription for a
  // product whose Subscribe & Save is hidden in the new market has to be
  // switched to one-time here. Markers come from cart-shared-items.liquid.
  async cleanUpHiddenSubscriptions() {
    // renderCartSections() runs inside reloadCart(), so this is re-entered by its own reload.
    if (this._cleaningUp || !this._hasHydrated) return;

    const lines = new Map();
    document.querySelectorAll("[data-subscription-unavailable]").forEach((el) => {
      const key = el.dataset.lineKey;
      if (!key || lines.has(key) || this._cleanedLineKeys.has(key)) return;
      lines.set(key, { key, message: el.dataset.message });
    });
    if (!lines.size) return;

    this._cleaningUp = true;
    try {
      const messages = [];

      for (const line of lines.values()) {
        this._cleanedLineKeys.add(line.key);
        const converted = await this._switchLineToOneTime(line.key);
        if (converted && !messages.includes(line.message)) messages.push(line.message);
      }

      if (!messages.length) return;

      this._addCartNotices(messages);
      await this.reloadCart({ reason: "mutation" });
      await this.handleRewards({ reason: "mutation" });

      if (messages.length && window.BeroA11y) window.BeroA11y.announce(messages.join(" "));
    } catch (err) {
      console.error("Subscription clean-up failed:", err);
    } finally {
      this._cleaningUp = false;
    }
  }

  // Drops the selling plan in place with one request, so a request that doesn't
  // complete changes nothing. The line index is re-read each time because it
  // shifts after an earlier line is converted or merged. Sending `properties`
  // would replace them, so it is left out.
  async _switchLineToOneTime(key) {
    try {
      const cart = await this.fetchCart();
      const index = cart.items.findIndex((item) => item.key === key);
      if (index === -1) return false;

      const body = JSON.stringify({ line: index + 1, quantity: cart.items[index].quantity, selling_plan: null });
      const res = await fetch("/cart/change.js", this.buildConfig(body));
      if (!res.ok) return false;

      const updated = await res.json();
      return !updated.items.some((item) => item.key === key && item.selling_plan_allocation);
    } catch (err) {
      console.error("Subscription switch failed:", err);
      return false;
    }
  }

  _readCartNotices() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(CART_NOTICES_KEY) || "[]");
      return Array.isArray(stored) ? stored.filter((m) => typeof m === "string") : [];
    } catch (e) {
      return [];
    }
  }

  _writeCartNotices() {
    try {
      if (this._cartNotices.length) sessionStorage.setItem(CART_NOTICES_KEY, JSON.stringify(this._cartNotices));
      else sessionStorage.removeItem(CART_NOTICES_KEY);
    } catch (e) {}
  }

  _addCartNotices(messages) {
    this._cartNotices = [...new Set([...this._cartNotices, ...messages])];
    this._writeCartNotices();
  }

  _clearCartNotices() {
    this._cartNotices = [];
    this._noticesSeenInFlyout = false;
    this._writeCartNotices();
    this._renderCartNotices();
  }

  // Notices live in the section HTML that every render replaces, so they are
  // re-inserted after each render until the shopper has seen them.
  _renderCartNotices() {
    const containers = [...document.querySelectorAll("[data-cart-notices]")];

    containers.forEach((container) => {
      container.replaceChildren(
        ...this._cartNotices.map((message) => {
          const p = document.createElement("p");
          p.className = "text-sm leading-snug p-3 mb-2 bg-cloud";
          p.textContent = message;
          return p;
        })
      );
    });

    if (!this._cartNotices.length) return;

    const flyoutPanel = this.flyout?.findPanel?.("cart");
    if (flyoutPanel?.classList.contains("active")) this._noticesSeenInFlyout = true;

    // Shown on the /cart page: drop them from storage, keep them for this page view's re-renders.
    if (this._isCartPage && containers.some((el) => !el.closest("site-flyout"))) {
      try {
        sessionStorage.removeItem(CART_NOTICES_KEY);
      } catch (e) {}
    }
  }

  // Once the bag has been open with the notices in it and is closed again, they are done.
  _watchCartFlyout() {
    const panelEl = this.flyout?.findPanel?.("cart");
    if (!panelEl || this._flyoutObserver) return;

    let wasOpen = panelEl.classList.contains("active");
    this._flyoutObserver = new MutationObserver(() => {
      const isOpen = panelEl.classList.contains("active");
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;

      if (isOpen) {
        if (this._cartNotices.length) this._noticesSeenInFlyout = true;
      } else if (this._noticesSeenInFlyout) {
        this._clearCartNotices();
      }
    });
    this._flyoutObserver.observe(panelEl, { attributes: true, attributeFilter: ["class"] });
  }

  buildConfig(body) {
    return {
      body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  async addBYOB(variantIds, _dynamicBoxId, _dynamicBoxIndex, selling_plan) {
    await this.removeBYOB(false);

    const items = variantIds.map((id, index) => ({
      id,
      quantity: 1,
      properties: { _dynamicBoxId, _dynamicBoxIndex, _itemIndex: index },
      ...(selling_plan ? { selling_plan } : {}),
    }));

    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);
    this.flyout.openPanel("cart");

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.announceCartError(response.description);
          return;
        }

        await this.loadCart();
        await this.handleRewards();
        await this._addMatchingGwpGifts();
      })
      .catch(() => {});
  }

  async removeItem(variantId, shouldReload = false) {
    const updates = { [variantId]: 0 };
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    await fetch("/cart/update", config)
      .then((res) => res.json())
      .then(() => this.reloadCart({ reason: "mutation" }));

    if (shouldReload) this.handleRewards({ reason: "mutation" });
    await this._addMatchingGwpGifts();
  }

  async removeBYOB(shouldRefresh = true) {
    const updates = {};

    this.cartData.items.forEach((item) => {
      if (item.properties && item.properties._dynamicBoxId) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length > 0) {
      const sections = this.getCartSections().map((s) => s.section);
      const body = JSON.stringify({ updates, sections });
      const config = this.buildConfig(body);

      await fetch("/cart/update", config).then((res) => res.json());

      if (shouldRefresh) {
        await this.reloadCart({ reason: "mutation" });
        this.handleRewards({ reason: "mutation" });
      }
    }
  }

  async refreshBeromasterDiscount() {
    const beromasterId = window.Bero.beromasterVariantId;
    const hasBeromaster = this.cartData.items.some((item) => +item.variant_id === +beromasterId);
    window.Bero.hasBeromasterInCart = hasBeromaster;
  }

  async applyMembershipDiscount() {
    const isVip = !!window?.Bero?.isRivoSubscriber || !!window?.Bero?.hasBeromasterInCart;
    if (!isVip) return false;

    const pct = window?.Bero?.discountBeromasterPercent;
    if (!pct) return false;

    if (!Array.isArray(this.cartData?.items) || this.cartData.items.length === 0) return false;

    const code = `BEROMASTER-${pct}`;
    const existing = (this.cartData.discount_codes ?? []).map((dc) => (dc.code ?? "").toUpperCase());
    if (existing.includes(code.toUpperCase())) return false;

    try {
      await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, {
        method: "GET",
        credentials: "same-origin",
      });
      await this.fetchCart();
      return true;
    } catch (err) {
      console.log("[BEROMASTER] apply error", err);
      return false;
    }
  }

  async addVariantToCart(id, quantity = 1, sellingPlan, properties, options = {}) {
    await this.refreshBeromasterDiscount();

    const items = [
      sellingPlan ? { id, quantity, selling_plan: sellingPlan, properties } : { id, quantity, properties },
    ];

    const { openCart = true } = options || {};

    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (openCart && this.flyout) this.flyout.openPanel("cart");

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then(async (response) => {
        const productDetailsController = document.querySelector("main-product");
        if (productDetailsController) productDetailsController.updateProductDetailsHTML();

        if (response.status) {
          this.announceCartError(response.description);
          return;
        }

        await this.reloadCart({ reason: "mutation" });
        await this.handleRewards({ reason: "mutation" });
        await this._addMatchingGwpGifts();
      })
      
  }

  async _addMatchingGwpGifts() {
    if (this._gwpProcessing) return;
    this._gwpProcessing = true;

    try {
      // Fetch GWP discounts from the Bero serverless app (single source of truth, replaces shop metafield).
      if (!window.Bero) window.Bero = {};
      if (!window.Bero._gwpDiscountsFetch) {
        window.Bero._gwpDiscountsFetch = fetch("https://bero-omega.vercel.app/api/gwp-discounts")
          .then((r) => (r.ok ? r.json() : []))
          .then((data) => {
            window.Bero.gwpDiscounts = Array.isArray(data) ? data : [];
            return window.Bero.gwpDiscounts;
          })
          .catch((err) => {
            console.log("[GWP] fetch error", err);
            window.Bero.gwpDiscounts = [];
            return [];
          });
      }
      const gwpDiscounts = await window.Bero._gwpDiscountsFetch;
      if (!Array.isArray(gwpDiscounts) || !gwpDiscounts.length) return;

      const cartData = await this.fetchCart();
      const cartCodes = (cartData?.discount_codes ?? []).map((dc) => dc.code.toUpperCase());
      const sessionCode = sessionStorage.getItem("alia_discount_code");
      const appliedCodes = sessionCode ? [...new Set([...cartCodes, sessionCode.toUpperCase()])] : cartCodes;

      // Toggle to enable verbose [GWP] logging in storefront.
      const GWP_DEBUG = false;
      const GWPLOG = GWP_DEBUG
        ? (...args) =>
            console.log(
              "[GWP]",
              ...args.map((a) => {
                if (a == null || typeof a === "string" || typeof a === "number" || typeof a === "boolean") return a;
                try {
                  return JSON.stringify(a, (_k, v) => (v instanceof Set ? [...v] : v));
                } catch {
                  return a;
                }
              }),
            )
        : () => {};

      const isGwpDiscount = (d) =>
        d.discount_type === "Automatic GWP Discount" || d.discount_type === "Discount Code GWP";
      const isCurrentlyApplicable = (d) => {
        if (d.discount_type === "Automatic GWP Discount") return true;
        if (d.discount_type === "Discount Code GWP") return appliedCodes.includes(d.code?.toUpperCase());
        return false;
      };
      const allGwpDiscounts = gwpDiscounts.filter(isGwpDiscount);
      if (!allGwpDiscounts.length) return;

      // Customer state
      const customerIsLoggedIn = window.Bero?.customerIsLoggedIn ?? false;
      const customerOrdersCount = window.Bero?.customerOrdersCount ?? 0;
      const currentMarketId = window.Bero?.currentMarketId ?? "";

      // Normalized customer tags
      const rawTags = window.Bero?.customerTags;
      const normalizedCustomerTags = Array.isArray(rawTags) ? rawTags.map((t) => t.trim().toLowerCase()) : [];

      // Cart state — total_price is in cents; threshold in metafield is in dollars
      const cartSubtotalDollars = (this.cartData?.total_price ?? 0) / 100;
      const cartItems = this.cartData?.items ?? [];

      GWPLOG("inputs", {
        cartCodes,
        sessionCode,
        appliedCodes,
        customerIsLoggedIn,
        customerOrdersCount,
        currentMarketId,
        rawCustomerTags: rawTags,
        normalizedCustomerTags,
        cartItems: cartItems.map((i) => ({
          variant_id: String(i.variant_id),
          quantity: i.quantity,
          line_price: i.line_price,
          properties: i.properties,
        })),
        gwpDiscounts,
      });

      // --- Cart query helpers ---

      const resolveVariantIds = (products) =>
        (products ?? [])
          .flatMap((p) => p.variants ?? [])
          .map((v) => {
            const gid = String(v.id ?? "");
            return gid.includes("/") ? gid.split("/").pop() : gid;
          })
          .filter(Boolean);

      const isVariantInCart = (variantId) => cartItems.some((item) => String(item.variant_id) === variantId);

      const hasAnyVariantInCart = (variantIds, minQty = 1) =>
        variantIds.some((id) => {
          const line = cartItems.find((item) => String(item.variant_id) === id);
          return line && line.quantity >= minQty;
        });

      const hasAllVariantsInCart = (variantIds, minQty = 1) =>
        variantIds.every((id) => {
          const line = cartItems.find((item) => String(item.variant_id) === id);
          return line && line.quantity >= minQty;
        });

      const hasNonExcludedItemInCart = (excludedVariantIds) =>
        cartItems.some((item) => !excludedVariantIds.includes(String(item.variant_id)));

      // --- Condition checks (mirrors checkout extension pipeline) ---

      // Subtotal excluding any gift variants (so adding the gift doesn't keep the threshold satisfied).
      const allGiftVariantIds = new Set(
        allGwpDiscounts.flatMap((d) => resolveVariantIds(d.value?.gift)),
      );
      const subtotalExcludingGiftsDollars =
        cartItems
          .filter((item) => !allGiftVariantIds.has(String(item.variant_id)))
          .reduce((sum, item) => sum + (item.line_price ?? item.final_line_price ?? 0), 0) / 100;

      const passesThresholdCheck = (threshold) => {
        if (threshold === undefined || threshold === null) return true;
        const t = Number(threshold);
        const s = subtotalExcludingGiftsDollars;
        return (s > 0 && s >= t) || (s <= 0 && t <= 0);
      };

      const passesRequiredProductsCheck = (value) => {
        if (!value.requiredProducts) return false;
        const requiredVariantIds = resolveVariantIds(value.requiredProducts);
        if (requiredVariantIds.length === 0) return true;
        const qty = Number(value.reqProductsQuantity) || 1;
        return value.allProductsRequiredCheck
          ? hasAllVariantsInCart(requiredVariantIds, qty)
          : hasAnyVariantInCart(requiredVariantIds, qty);
      };

      const passesExcludedProductsCheck = (value) => {
        if (!value.excludedProducts) return true;
        const excludedVariantIds = resolveVariantIds(value.excludedProducts);
        if (excludedVariantIds.length === 0) return true;
        if (value.onlyProductsExcludedCheck) {
          return hasNonExcludedItemInCart(excludedVariantIds);
        }
        return !hasAnyVariantInCart(excludedVariantIds);
      };

      const passesAllConditions = (discount) => {
        const { value } = discount;
        const label = discount.code || discount.discount_type;

        if (value.registeredCheck && !customerIsLoggedIn) {
          GWPLOG(`fail registeredCheck`, label);
          return false;
        }

        if (value.firstOrder && customerOrdersCount !== value.firstOrderValue) {
          GWPLOG(`fail firstOrder`, label, { customerOrdersCount, expected: value.firstOrderValue });
          return false;
        }

        let requiredTags = value.tags ?? [];
        if (typeof requiredTags === "string") requiredTags = requiredTags ? [requiredTags] : [];
        requiredTags = requiredTags.map((t) => t.trim().toLowerCase()).filter(Boolean);
        if (requiredTags.length > 0 && !requiredTags.some((tag) => normalizedCustomerTags.includes(tag))) {
          GWPLOG(`fail tags`, label, { requiredTags, normalizedCustomerTags });
          return false;
        }

        if (!passesThresholdCheck(value.threshold)) {
          GWPLOG(`fail threshold`, label, { threshold: value.threshold, subtotalExcludingGiftsDollars });
          return false;
        }

        if (!value.markets.includes(currentMarketId)) {
          GWPLOG(`fail market`, label, { currentMarketId, allowed: value.markets });
          return false;
        }

        if (!passesRequiredProductsCheck(value)) {
          GWPLOG(`fail requiredProducts`, label);
          return false;
        }

        if (!passesExcludedProductsCheck(value)) {
          GWPLOG(`fail excludedProducts`, label);
          return false;
        }

        return true;
      };

      // --- Collect gifts to add / remove (matching checkout extension pattern) ---

      const allGiftIds = new Set();
      const giftsToAdd = new Set();

      for (const discount of allGwpDiscounts) {
        const label = discount.code || discount.discount_type;
        const giftVariantIds = resolveVariantIds(discount.value?.gift);
        GWPLOG(`evaluating`, label, { giftVariantIds, type: discount.discount_type });
        if (!giftVariantIds.length) {
          GWPLOG(`skip ${label}: no gift variants`);
          continue;
        }

        // Always track gift ids so we can remove the gift if it's no longer applicable.
        for (const id of giftVariantIds) allGiftIds.add(id);

        if (!isCurrentlyApplicable(discount)) {
          GWPLOG(`skip ${label}: not currently applicable (code not applied)`);
          continue;
        }
        if (!passesAllConditions(discount)) continue;

        // Edge case guard: skip if required products IS the gift
        if (JSON.stringify(discount.value.requiredProducts) === JSON.stringify(discount.value.gift)) continue;

        for (const id of giftVariantIds) giftsToAdd.add(id);
      }

      // Orphan cleanup: any cart line previously added by us (has _gwp property) whose
      // variant is no longer in giftsToAdd should be removed — this catches the case
      // where its source discount was deactivated/deleted entirely.
      const orphanedGwpVariants = cartItems
        .filter((item) => item.properties && item.properties._gwp)
        .map((item) => String(item.variant_id))
        .filter((id) => !giftsToAdd.has(id));

      const toAdd = [...giftsToAdd].filter((id) => !isVariantInCart(id));
      const toRemove = [
        ...new Set([
          ...[...allGiftIds].filter((id) => !giftsToAdd.has(id) && isVariantInCart(id)),
          ...orphanedGwpVariants,
        ]),
      ];

      GWPLOG("result", {
        allGiftIds: [...allGiftIds],
        giftsToAdd: [...giftsToAdd],
        orphanedGwpVariants,
        toAdd,
        toRemove,
        cartVariants: cartItems.map((i) => ({ variant_id: String(i.variant_id), _gwp: i.properties?._gwp })),
        subtotalExcludingGiftsDollars,
      });

      // Split GWP items with quantity > 1: keep 1 as free gift, move extras to a new paid line
      const gwpSplits = cartItems.filter((item) => item.properties?._gwp && item.quantity > 1);
      for (const item of gwpSplits) {
        const extraQty = item.quantity - 1;
        // Reduce GWP line to 1
        await fetch("/cart/change", this.buildConfig(JSON.stringify({ id: item.key, quantity: 1 })))
          .then((r) => r.json())
          .catch((err) => console.log("GWP split reduce error:", err.message));
        // Add extras as a separate paid line (no _gwp property)
        await fetch("/cart/add", this.buildConfig(JSON.stringify({
          items: [{ id: item.variant_id, quantity: extraQty }],
        })))
          .then((r) => r.json())
          .catch((err) => console.log("GWP split add error:", err.message));
      }

      if (toAdd.length === 0 && toRemove.length === 0 && gwpSplits.length === 0) return;

      // Remove ineligible gifts first (single cart/update call)
      if (toRemove.length > 0) {
        const updates = Object.fromEntries(toRemove.map((id) => [id, 0]));
        await fetch("/cart/update", this.buildConfig(JSON.stringify({ updates })))
          .then((r) => r.json())
          .catch((err) => console.log("GWP gift remove error:", err.message));
      }

      // Add eligible gifts
      for (const variantId of toAdd) {
        const code = allGwpDiscounts.find((d) => resolveVariantIds(d.value?.gift).includes(variantId))?.code;
        await fetch(
          "/cart/add",
          this.buildConfig(
            JSON.stringify({
              items: [{ id: variantId, quantity: 1, properties: { _gwp: code } }],
            }),
          ),
        )
          .then((r) => r.json())
          .catch((err) => console.log("GWP gift add error:", err.message));
      }

      await this.reloadCart({ reason: "mutation" });
      await this.handleRewards({ reason: "mutation" });
    } finally {
      this._gwpProcessing = false;
      this._applyGwpCodePriceOverrides();
    }
  }

  _applyGwpCodePriceOverrides() {
    const sessionCode = sessionStorage.getItem("alia_discount_code");
    if (!sessionCode) return;

    const gwpDiscounts = window.Bero?.gwpDiscounts;
    if (!Array.isArray(gwpDiscounts) || !gwpDiscounts.length) return;

    const matchingDiscounts = gwpDiscounts.filter(
      (d) => d.discount_type === "Discount Code GWP" && d.code?.toUpperCase() === sessionCode.toUpperCase(),
    );
    if (!matchingDiscounts.length) return;

    const giftVariantToCode = new Map();
    for (const discount of matchingDiscounts) {
      const code = discount.code || sessionCode;
      for (const gift of discount.value?.gift ?? []) {
        for (const variant of gift.variants ?? []) {
          const gid = String(variant.id ?? "");
          const id = gid.includes("/") ? gid.split("/").pop() : gid;
          if (id) giftVariantToCode.set(id, code);
        }
      }
    }
    if (giftVariantToCode.size === 0) return;

    document.querySelectorAll("cart-item[data-selected-variant]").forEach((el) => {
      let variant;
      try {
        variant = JSON.parse(el.dataset.selectedVariant);
      } catch {
        return;
      }
      const variantId = String(variant.id);
      if (!giftVariantToCode.has(variantId)) return;

      // Only apply override to GWP-added items (with _gwp property), not manually-added ones
      const idx = parseInt(el.dataset.idx, 10);
      const cartItem = this.cartData?.items?.[idx - 1];
      if (!cartItem || !cartItem.properties?._gwp) return;

      const originalPrice = variant.price;
      const code = giftVariantToCode.get(variantId);
      const totalsEl = el.querySelector(".cart-item__totals");
      if (!totalsEl) return;

      // Prefer structured price elements if available
      const priceActive = totalsEl.querySelector("[data-price-active]");
      const priceCompare = totalsEl.querySelector("[data-price-compare]");

      if (priceActive) {
        priceActive.textContent = formatMoneyNoTrailingZeros(0);
        if (priceCompare && originalPrice > 0) {
          priceCompare.classList.remove("hidden");
          priceCompare.classList.add("line-through", "opacity-50");
          priceCompare.textContent = formatMoneyNoTrailingZeros(originalPrice);
        }
      } else {
        // Fallback: override the totals inner HTML directly
        const priceWrap = totalsEl.querySelector("[data-price-wrap]") || totalsEl;
        const innerDiv = priceWrap.querySelector("div") || priceWrap;
        if (!innerDiv.dataset.gwpOverridden) {
          innerDiv.dataset.gwpOverridden = "true";
          innerDiv.innerHTML =
            `<span class="line-through opacity-50">${formatMoneyNoTrailingZeros(originalPrice)}</span>` +
            ` <span>${formatMoneyNoTrailingZeros(0)}</span>`;
        }
      }

      // Skip adding JS label if Liquid already rendered it (data-gwp-discount-label in server HTML)
      const priceWrap = totalsEl.querySelector("[data-price-wrap]") || totalsEl;
      if (!priceWrap.querySelector("[data-gwp-discount-label]") && originalPrice > 0) {
        const label = document.createElement("div");
        label.setAttribute("data-gwp-discount-label", "");
        label.className = "mt-1 text-xs opacity-70 text-right";
        label.textContent = `${code} (-${formatMoneyNoTrailingZeros(originalPrice)})`;
        priceWrap.appendChild(label);
      }
    });
  }

  async fetchSections(sectionIds, sectionsUrl = "/cart") {
    if (!sectionIds.length) return {};
    const qs = new URLSearchParams();
    qs.set("sections", sectionIds.join(","));
    qs.set("sections_url", sectionsUrl);
    const res = await fetch(`/?${qs.toString()}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Section fetch failed: ${res.status} ${res.statusText} ${text}`);
    }
    return res.json();
  }

  async reloadCart({ reason = "mutation" } = {}) {
    await this.fetchCart();
    await this.refreshBeromasterDiscount();
    await this.applyMembershipDiscount();

    if (this._hasHydrated && reason === "mutation") {
      const productDetailsController = document.querySelector("main-product");
      if (productDetailsController) {
        productDetailsController.updateProductDetailsHTML();
      }

      const quickAddBeromaster = document.querySelector("quick-add-beromaster");
      if (quickAddBeromaster) quickAddBeromaster.updateTextContent();
    }

    const shouldRenderSections = !(this._isCartPage && reason === "init");

    if (shouldRenderSections) {
      const sections = this.getCartSections().map((s) => s.section);
      const response = await this.fetchSections(sections);
      this.renderCartSections(response);
    }

    if (reason === "mutation") {
      await this.refreshRecommendations();
    }

    this.updateCartCount();

    if (!(this._isCartPage && reason === "init")) {
      applyPricingState();
    }

    this._applyGwpCodePriceOverrides();

    this.announceCartState(reason);
  }

  // Politely announce cart changes to screen readers (item added / removed /
  // updated / emptied). Never fires on initial load, only on real mutations.
  announceCartState(reason) {
    const count = Number(this.cartData?.item_count ?? 0);

    if (window.BeroA11y && this._hasHydrated && reason === "mutation") {
      const prev = typeof this._prevAnnouncedCount === "number" ? this._prevAnnouncedCount : count;
      let message;
      if (count === 0) {
        message = "Your bag is now empty.";
      } else {
        let verb = "Bag updated.";
        if (count > prev) verb = "Item added.";
        else if (count < prev) verb = "Item removed.";
        message = `${verb} ${count} ${count === 1 ? "item" : "items"} in your bag.`;
      }
      window.BeroA11y.announce(message);
    }

    this._prevAnnouncedCount = count;
  }

  // Surface an add/quantity error assertively (replaces a call to an
  // undefined Dawn method that previously threw).
  announceCartError(message) {
    const text =
      (typeof message === "string" && message.trim()) ||
      "Sorry, we couldn't update your bag. Please try again.";
    if (window.BeroA11y) window.BeroA11y.announce(text, { assertive: true });
    else console.warn("[cart]", text);
  }

  async refreshRecommendations() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("_t", Date.now());
      const res = await fetch(url.toString(), { credentials: "same-origin" });
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      document.querySelectorAll("[data-cart-section='recommendations']").forEach((el) => {
        const source = el.dataset.recSource;
        if (!source) return;
        const freshEl = doc.querySelector(
          `[data-cart-section='recommendations'][data-rec-source='${source}']`
        );
        if (!freshEl) return;

        // Destroy old Swiper before replacing HTML
        const oldCarousel = el.querySelector("site-carousel");
        if (oldCarousel && oldCarousel.swiper) {
          oldCarousel.swiper.destroy(true, true);
        }

        el.innerHTML = freshEl.innerHTML;
      });
    } catch (e) {
      // silently fail – recommendations stay as-is
    }
  }

  updateCartCount() {
    const count = Number(this.cartData?.item_count ?? 0);
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = String(count);
    });
  }

  async updateQuantity(line, quantity) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then(async () => {
        await this.reloadCart({ reason: "mutation" });
        await this.handleRewards({ reason: "mutation" });
        await this._addMatchingGwpGifts();
      })
      .catch((err) => console.log(err.message));
  }

  async updateCart(updates, shouldRefresh = true) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/update", config)
      .then((response) => response.json())
      .then(async (response) => {
        await this.reloadCart({ reason: "mutation" });
        if (shouldRefresh) this.renderCartSections(response.sections);
        await this._addMatchingGwpGifts();
      })
      .catch((err) => console.log(err.message));
  }

  async clearCart(shouldRefresh = true) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates: {}, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/clear", config)
      .then((response) => response.json())
      .then(async (response) => {
        await this.reloadCart({ reason: "mutation" });
        if (shouldRefresh) this.renderCartSections(response.sections);
        await this._addMatchingGwpGifts();
      })
      .catch(() => {});
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    this.cartData = await resp.json();
    return this.cartData;
  }

  async handleRewards({ reason = "mutation" } = {}) {
    // Update ALL <cart-rewards> elements globally (cart page + flyout)
    const allRewards = document.querySelectorAll("cart-rewards");
    if (!allRewards.length) return;

    const hideAll = (nodes) => nodes.forEach((n) => n?.classList.add("hidden"));
    const show = (node) => node?.classList.remove("hidden");

    const subtotal = this.cartData?.total_price ?? 0;

    const hasSubscription =
      Array.isArray(this.cartData?.items) &&
      this.cartData.items.some((item) => item?.selling_plan_allocation || item?.selling_plan || item?.selling_plan_id);

    // Count UK BYOB items for 24-pack free shipping check
    const byobItemCount = Array.isArray(this.cartData?.items)
      ? this.cartData.items.filter((i) => i.properties && i.properties._dynamicBoxId).length
      : 0;

    const detectCurrencyLocale = () => {
      const ds = (allRewards[0] && allRewards[0].dataset) || {};
      const country = (ds.country || (window.Shopify && Shopify.country) || "").toUpperCase();
      const currency =
        ds.currency ||
        (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        (country === "GB" ? "GBP" : "USD");
      const locale =
        ds.locale ||
        (document.documentElement.lang || "").replace("_", "-") ||
        (currency === "GBP" ? "en-GB" : "en-US");
      return { currency, locale };
    };

    const { currency: defaultCurrency, locale: defaultLocale } = detectCurrencyLocale();

    const money = (cents) =>
      new Intl.NumberFormat(defaultLocale, {
        style: "currency",
        currency: defaultCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
        .format((Number(cents) || 0) / 100)
        .replace(/\.00$/, "");

    // Captured once for a single, de-duplicated screen-reader announcement
    // (the snippet renders in both the cart page and the always-present
    // flyout, so a per-element aria-live would announce twice).
    let rewardStatus = null;
    let rewardRemaining = "";

    allRewards.forEach((rewards) => {
      const detect = () => {
        const ds = rewards.dataset || {};
        const country = (ds.country || (window.Shopify && Shopify.country) || "").toUpperCase();
        const currency =
          ds.currency ||
          (window.Shopify && Shopify.currency && Shopify.currency.active) ||
          (country === "GB" ? "GBP" : "USD");
        const locale =
          ds.locale ||
          (document.documentElement.lang || "").replace("_", "-") ||
          (currency === "GBP" ? "en-GB" : "en-US");
        return { currency, locale, country };
      };

      const { currency, locale, country } = detect();

      const localMoney = (cents) =>
        new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
          .format((Number(cents) || 0) / 100)
          .replace(/\.00$/, "");

      const els = {
        empty: rewards.querySelector('[data-status="empty"]'),
        pending: rewards.querySelector('[data-status="pending"]'),
        earned: rewards.querySelector('[data-status="earned"]'),
        shippingRemaining: rewards.querySelector(".shipping-remaining"),
        bar: rewards.querySelector(".percentage-bar"),
      };

      hideAll([els.empty, els.pending, els.earned]);

      const shippingThreshold = Number(rewards.dataset.shippingThreshold || 0);
      const has24PackFreeShipping = country === "GB" && byobItemCount >= 6;
      const shippingMet = hasSubscription || has24PackFreeShipping || shippingThreshold === 0 || subtotal >= shippingThreshold;

      if (shippingMet) {
        show(els.earned);
        if (els.shippingRemaining) els.shippingRemaining.textContent = "";
        rewardStatus = "earned";
      } else if (subtotal === 0 && !hasSubscription) {
        show(els.empty);
        const remaining = Math.max(shippingThreshold - subtotal, 0);
        if (els.shippingRemaining) els.shippingRemaining.textContent = localMoney(remaining);
        rewardStatus = "empty";
        rewardRemaining = localMoney(remaining);
      } else {
        const remaining = Math.max(shippingThreshold - subtotal, 0);
        if (els.shippingRemaining) els.shippingRemaining.textContent = localMoney(remaining);
        show(els.pending);
        rewardStatus = "pending";
        rewardRemaining = localMoney(remaining);
      }

      const progress = hasSubscription || has24PackFreeShipping
        ? 100
        : shippingThreshold === 0
          ? 100
          : Math.min((subtotal / Math.max(shippingThreshold, 1)) * 100, 100);

      if (els.bar) els.bar.style.width = `${progress}%`;
    });

    // Announce free-shipping progress once, only when the status actually
    // changes (crossing the threshold), to avoid noise on every cart reload.
    if (
      window.BeroA11y &&
      this._hasHydrated &&
      reason === "mutation" &&
      rewardStatus &&
      rewardStatus !== this._prevRewardsStatus
    ) {
      const msg =
        rewardStatus === "earned"
          ? "You've unlocked free shipping."
          : `You're ${rewardRemaining} away from free shipping.`;
      window.BeroA11y.announce(msg);
    }
    this._prevRewardsStatus = rewardStatus;

    const hasBeromaster =
      Array.isArray(this.cartData?.items) && this.cartData.items.some((item) => item.handle === "beromaster");

    if (hasBeromaster && window?.Bero?.isRivoSubscriber) {
      const beromaster = this.cartData.items.filter((item) => item.handle === "beromaster");
      this.removeItem(`${beromaster[0].variant_id}`, true);
    }

    if (!hasBeromaster && !window?.Bero?.isRivoSubscriber) {
      for (const item of this.cartData.items) {
        if (item.properties?.preOrder) this.removeItem(item.id, true);
      }
    }

    if (!this._hasHydrated || reason === "init") return;

    // Skip the PDP ATC (#add-to-cart-button): main-product re-renders it from the
    // server on every cart mutation (see reloadCart), with the correct label and state.
    document.querySelectorAll("button[data-add-membership]:not(#add-to-cart-button)").forEach((button) => {
      if (hasBeromaster) {
        button.innerHTML = "Already in Bag";
        button.disabled = true;
      } else {
        button.innerHTML = `Join today - ${money(5500)}/yr`;
        button.removeAttribute("disabled");
      }
    });
  }

  async removePreOrderWithoutBeromaster(hasBeromaster) {
    if (!hasBeromaster && !window?.Bero?.isRivoSubscriber) {
      for (const item of this.cartData.items) {
        if (item.properties?.preOrder) this.removeItem(item.id, true);
      }
    }
  }

  async removeDiscountIfNotBeomaster(hasBeromaster) {
    if (!hasBeromaster && !window?.Bero?.isRivoSubscriber) {
      await fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ discount: "" }),
      })
        .then((response) => response.json())
        .catch((err) => console.log(err.message));
    }

    if (window?.Bero?.isRivoSubscriber) {
      await fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: {} }),
      })
        .then((response) => response.json())
        .catch((err) => console.log(err.message));
    }
  }

  async removeBeromasterInLogin(hasBeromaster) {
    if (hasBeromaster && window?.Bero?.isRivoSubscriber) {
      const beromaster = cart.items.filter((item) => item.handle === "beromaster");
      this.removeItem(`${beromaster[0].variant_id}`, true);
    }
  }

  async loadCart() {
    if (this._isCartPage) {
      await this.fetchCart();
      await this.refreshBeromasterDiscount();
      const appliedMembershipDiscount = await this.applyMembershipDiscount();
      this.updateCartCount();
      this.handleRewards({ reason: "init" });
      await this._addMatchingGwpGifts();
      if (appliedMembershipDiscount) {
        await this.reloadCart({ reason: "mutation" });
      }
      return;
    }

    await this.reloadCart({ reason: "init" });

    const hasBeromaster =
      Array.isArray(this.cartData?.items) && this.cartData.items.some((item) => item.handle === "beromaster");

    await this.removeBeromasterInLogin(hasBeromaster);
    await this.removePreOrderWithoutBeromaster(hasBeromaster);
    await this.removeDiscountIfNotBeomaster(hasBeromaster);

    await this.reloadCart({ reason: "init" });
    await this._addMatchingGwpGifts();
  }
}

customElements.define("site-cart", SiteCart);

/*
Number Input
*/

class NumberInput extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new Event("change", { bubbles: true });
  }

  connectedCallback() {
    this.input = this.querySelector("input");

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.target.closest("button");

    if (button) {
      button.name === "increment" ? this.input.stepUp() : this.input.stepDown();

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }
}

customElements.define("number-input", NumberInput);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.becomeMember = this.dataset.becomeMember === "true";

    if (this.becomeMember) {
      this.showLogin();
    } else {
      this.addProductToCart();
    }
  }

  showLogin() {
    const loginButtons = this.querySelectorAll('[name="login"]');
    loginButtons.forEach((button) => {
      const hashUrl = document.createElement("hash-url");
      hashUrl.setAttribute("data-hash", "rivo");

      button.replaceWith(hashUrl);

      hashUrl.appendChild(button);
    });
  }

  addProductToCart() {
    this.addEventListener("click", async (e) => {
      // Focusable-but-disabled buttons (e.g. "Already in Bag") must not add anything.
      if (e.target.closest('button[aria-disabled="true"]')) {
        e.preventDefault();
        return;
      }

      if (!e.target.closest('button[type="submit"]') && !e.target.closest("form")) {
        return;
      }

      const form = e.target.closest("form");
      if (!form) return;

      e.preventDefault();
      const $id = form.querySelector('[name="id"]');
      const $qty = this.querySelector('[name="quantity"]');
      const $sellingPlan = form.querySelector('[name="selling_plan"]');
      const preOrderInput = this.querySelector('[name="properties[_preorder]"]');
      const preOrderValue = preOrderInput ? preOrderInput.value === "true" : false;
      const qty = $qty ? $qty.value : 1;
      const sellingPlan = $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value.trim() : null;

      const recipientFormV2 = document.querySelector("recipient-form-v2");
      let properties = recipientFormV2 ? recipientFormV2.propertiesValues() : {};
      properties = preOrderValue ? { ...properties, _preorder: preOrderValue } : properties;

      const giftCardProps = Array.from(this.querySelectorAll("[data-gift-card-property]"));

      if (giftCardProps.length) {
        giftCardProps.forEach((input) => {
          const propName = input.dataset.giftCardProperty;
          properties = { ...properties, [propName]: input.value };
        });
      }

      const isValidProperties = recipientFormV2 ? recipientFormV2.validProperties() : true;

      const addBeroToggle = document.getElementById("add-beromaster-toggle");
      const addBero = addBeroToggle && addBeroToggle.checked;
      const beroVariantId = addBeroToggle ? addBeroToggle.dataset.beromasterVariantId : null;

      if ($id && isValidProperties) {
        const cart = SiteCart._primaryInstance || document.querySelector("site-cart");
        if (cart) {
          const btn = e.target.closest('button[type="submit"]');
          const icon = btn?.querySelector("[data-icon]");
          const loader = btn?.querySelector("[data-loader]");
          if (icon) icon.classList.add("hidden");
          if (loader) loader.classList.remove("hidden");

          await cart.addVariantToCart($id.value, qty, sellingPlan, properties);

          if (addBero && beroVariantId) {
            const membershipProps = {
              ...(properties || {}),
              beromaster_membership: true,
            };
            await cart.addVariantToCart(beroVariantId, 1, null, membershipProps);
          }

          if (icon) icon.classList.remove("hidden");
          if (loader) loader.classList.add("hidden");
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Toggle Membership
*/

class ToggleMembership extends HTMLElement {
  connectedCallback() {
    this.siteCart = SiteCart._primaryInstance || document.querySelector("site-cart");
    this.ignoreSelector = (this.getAttribute("ignore-selector") || "").trim();

    this.checkboxSelector = (this.getAttribute("checkbox-selector") || "input[type='checkbox']").trim();

    this.triggerSelector = (this.getAttribute("trigger-selector") || "[data-add-membership]").trim();

    this.checkbox = this.querySelector(this.checkboxSelector);
    this.trigger = this.querySelector(this.triggerSelector);

    this.variantInput = this.querySelector("#variant-id");
    this.planInput = this.querySelector("#selling-plan-id");

    this.variantId = this.variantInput ? parseInt(this.variantInput.value, 10) : null;

    this.planId = this.planInput && this.planInput.value ? parseInt(this.planInput.value, 10) : null;

    this.loader = this.querySelector("[data-toggle-membership-loader]");
    this.priceBlock = this.querySelector("[data-toggle-membership-price]");

    this.isUpdating = false;

    this.onClickCapture = this.onClickCapture.bind(this);
    this.onChange = this.onChange.bind(this);

    this.addEventListener("click", this.onClickCapture, true);

    if (this.checkbox) {
      this.checkbox.addEventListener("change", this.onChange);
    } else {
      this.toggleAttribute("data-checked", false);
    }

    if (!this.checkbox && !this.trigger) {
      console.warn(
        "<toggle-membership>: No checkbox or trigger found. Add a checkbox inside the component or add a button with [data-add-membership], or set trigger-selector/checkbox-selector.",
      );
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClickCapture, true);
    if (this.checkbox) this.checkbox.removeEventListener("change", this.onChange);
  }

  shouldIgnoreClick(target) {
    if (!this.ignoreSelector) return false;
    return !!target.closest(this.ignoreSelector);
  }

  showLoader() {
    if (this.loader) this.loader.classList.remove("hidden");
  }

  hideLoader() {
    if (this.loader) this.loader.classList.add("hidden");
  }

  hidePrices() {
    if (this.priceBlock) this.priceBlock.classList.add("hidden");
  }

  setChecked(checked) {
    if (this.checkbox) this.checkbox.checked = !!checked;
    this.toggleAttribute("data-checked", !!checked);
  }

  onClickCapture(e) {
    if (this.isUpdating) return;
    if (this.shouldIgnoreClick(e.target)) return;

    const clickedCheckbox = this.checkbox && (e.target === this.checkbox || e.target.closest(this.checkboxSelector));

    const clickedTrigger = this.trigger && (e.target === this.trigger || e.target.closest(this.triggerSelector));

    if (clickedCheckbox) return;

    if (clickedTrigger) {
      e.preventDefault();
      const next = this.checkbox ? this.checkbox.checked : this.hasAttribute("data-checked");
      const newVal = !next;
      this.setChecked(newVal);
      this.commit(newVal);
      return;
    }

    if (!this.checkbox) return;

    e.preventDefault();
    const next = !this.checkbox.checked;
    this.setChecked(next);
    this.commit(next);
  }

  onChange(e) {
    if (this.isUpdating) return;
    this.commit(e.currentTarget.checked);
  }

  async commit(checked) {
    if (this.isUpdating) return;

    this.isUpdating = true;

    try {
      if (checked) {
        this.showLoader();
        this.hidePrices();
        const flyout = document.querySelector("site-flyout");
        if (flyout) {
          flyout.closePanel("free-shipping");
          flyout.closePanel("membership");
        }
        await this.addMembership();
      } else {
        this.showLoader();
        await this.removeMembership();
      }
    } catch (err) {
      this.setChecked(!checked);
    } finally {
      this.isUpdating = false;
      this.hideLoader();
    }
  }

  async addMembership() {
    if (!this.siteCart || !this.variantId) return;
    await this.siteCart.addVariantToCart(this.variantId, 1, this.planId, null, {
      openCart: false,
    });
  }

  async removeMembership() {
    if (!this.siteCart) return;

    const membershipItem = this.siteCart.querySelector("cart-item[data-is-membership]");
    if (membershipItem && typeof membershipItem.removeItem === "function") {
      await membershipItem.removeItem();
      return;
    }

    const cart = await this.siteCart.fetchCart();
    const membershipLine = Array.isArray(cart?.items) ? cart.items.find((i) => i?.handle === "beromaster") : null;

    if (membershipLine && membershipLine.key) {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ updates: { [membershipLine.key]: 0 } }),
      });
      await this.siteCart.reloadCart();
    }
  }
}

customElements.define("toggle-membership", ToggleMembership);

/*
Accordion Item
*/

class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;
  }

  connectedCallback() {
    this.startOpen = this.dataset.startOpen === "true";
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.content = this.querySelector(".content");
    this.arrow = this.querySelector(".accordion-arrow");
    this.plusIcon = this.querySelector(".accordion-plus");

    if (!this.details || !this.content || !this.summary) return;

    this.content.style.overflow = "hidden";
    this.content.style.boxSizing = "border-box";

    if (this.startOpen) {
      this.details.open = true;
      this.content.style.height = "auto";
      this.content.style.opacity = "1";
    } else {
      this.content.style.height = "0";
      this.content.style.opacity = "0";
    }

    this.updateIcon(this.details.open);

    this.summary.addEventListener("click", (e) => {
      e.preventDefault();
      if (!this.isAnimating) this.toggle();
    });
  }

  toggle() {
    this.details.open ? this.close() : this.open();
  }

  open() {
    this.isAnimating = true;
    this.details.open = true;

    this.content.style.height = "0";
    this.content.style.opacity = "0";
    void this.content.offsetHeight;

    const endHeight = this.content.scrollHeight;

    const animation = this.content.animate(
      [
        { height: "0px", opacity: 0 },
        { height: `${endHeight}px`, opacity: 1 },
      ],
      { duration: 300, easing: "ease" },
    );

    this.updateIcon(true);

    this.dispatchEvent(
      new CustomEvent("accordion-item:open", {
        bubbles: true,
        detail: { index: Number(this.dataset.index || 0) },
      }),
    );

    animation.onfinish = () => {
      this.content.style.height = "auto";
      this.content.style.opacity = "1";
      this.isAnimating = false;
    };
  }

  close() {
    this.isAnimating = true;

    this.updateIcon(false);

    const startHeight = this.content.scrollHeight;

    const animation = this.content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: "0px", opacity: 0 },
      ],
      { duration: 300, easing: "ease" },
    );

    animation.onfinish = () => {
      this.content.style.height = "0";
      this.content.style.opacity = "0";
      this.details.open = false;
      this.isAnimating = false;
    };
  }

  updateIcon(isOpen) {
    if (this.arrow) {
      this.arrow.style.transform = isOpen ? "rotate(180deg)" : "rotate(0deg)";
    }

    if (this.plusIcon) {
      this.plusIcon.style.transform = isOpen ? "rotate(45deg)" : "rotate(0deg)";
    }
  }
}

customElements.define("accordion-item", AccordionItem);

/*
Hash URL
*/

class HashURL extends HTMLElement {
  connectedCallback() {
    const hash = this.dataset.hash;

    this.querySelector("button").addEventListener("click", () => {
      const baseUrl = window.location.href.split("#")[0];
      window.location.href = `${baseUrl}#${hash}`;
    });
  }
}

customElements.define("hash-url", HashURL);

/*
Currency symbol
*/

const updateCurrencySymbols = () => {
  const currencySymbol = window?.Bero?.storeCurrencySymbol || "$";

  const priceElements = document.querySelectorAll(".dynamic-price-symbol");
  priceElements.forEach((element) => {
    const originalText = element.textContent.trim();
    const updatedText = originalText.replace(/[$£]/g, currencySymbol);
    element.textContent = updatedText;
  });
};

// Remove all BYOB bundle items from cart (used before market switch).
// Delegates to SiteCart.removeBYOB() when available, falls back to direct fetch.
async function clearBundleItemsFromCart() {
  try {
    const siteCart = (typeof SiteCart !== "undefined" && SiteCart._primaryInstance)
      || document.querySelector("site-cart");
    if (siteCart?.removeBYOB) {
      await siteCart.removeBYOB(false);
      return;
    }
    // Fallback: SiteCart not initialized yet
    const res = await fetch('/cart.js');
    const cart = await res.json();
    const updates = {};
    let found = false;
    cart.items.forEach(item => {
      if (item.properties?._dynamicBoxId) {
        updates[item.key] = 0;
        found = true;
      }
    });
    if (!found) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
  } catch (err) {
    console.error('[Bero] Failed to clear bundle items:', err);
  }
}
window.__clearBundleItemsFromCart = clearBundleItemsFromCart;

const handleCountryRedirect = () => {
  const blockedUkPaths = ["/collections/bero-collection"];

  const currentCurrency = window?.Bero?.storeCurrencySymbol || "$";
  const isUK = currentCurrency === "£";
  const isUS = !isUK;

  // Redirect mapped products to their market-specific sibling
  const productMatch = location.pathname.match(/^\/products\/([^/?#]+)/);
  if (productMatch) {
    const handle = productMatch[1];
    if (isUK && PRODUCT_MARKET_MAP_US_TO_UK[handle]) {
      window.location.href = `/products/${PRODUCT_MARKET_MAP_US_TO_UK[handle]}`;
      return;
    }
    if (isUS && PRODUCT_MARKET_MAP_UK_TO_US[handle]) {
      window.location.href = `/products/${PRODUCT_MARKET_MAP_UK_TO_US[handle]}`;
      return;
    }
  }

  if (isUK && blockedUkPaths.includes(location.pathname)) {
    window.location.href = "/";
    return;
  }

  document.addEventListener("click", (e) => {
    const link = e.target.closest(".country-selector__list .disclosure__link");
    if (!link) return;

    if (link.dataset.value === "GB" && blockedUkPaths.includes(location.pathname)) {
      e.preventDefault();
      const form = link.closest("form");
      if (!form) return (location.href = "/");

      form.querySelector("input[name='country_code']").value = "GB";
      form.querySelector("input[name='return_to']").value = "/";
      form.submit();
    }
  });
};

document.addEventListener("DOMContentLoaded", () => {
  const maps = parseProductMarketMappings();
  PRODUCT_MARKET_MAP_US_TO_UK = maps.usToUk;
  PRODUCT_MARKET_MAP_UK_TO_US = maps.ukToUs;

  handleCountryRedirect();
  updateCurrencySymbols();
});

class QuickAddBeromaster extends HTMLElement {
  connectedCallback() {
    this.hasCustomer = this.dataset.customer != "";
    this.button = this.querySelector("button");
    this.addEventListener("click", () => {
      if (this.hasCustomer) {
        window.location.href = "/products/beromaster";
      } else {
        !window?.Bero?.hasBeromasterInCart && this.addMembership();
      }
    });
  }

  async addMembership() {
    const siteCart = SiteCart._primaryInstance || document.querySelector("site-cart");
    const sellingPlan = document.querySelector("#selling-plan-id");
    const sellingPlanValue = sellingPlan.value ? sellingPlan.value.trim() : null;
    const beromasterVariantId = `${window?.Bero?.beromasterVariantId}`;
    try {
      siteCart && (await siteCart.addVariantToCart(beromasterVariantId, 1, sellingPlanValue));
      this.updateTextContent();
    } catch (err) {
      console.log(err);
    }
  }

  async updateTextContent() {
    if (this.hasCustomer) {
      this.button.textContent = "Already a member";
    } else {
      if (window?.Bero?.hasBeromasterInCart) {
        this.button.textContent = "Already in Bag";
        this.button.disabled = true;
      } else {
        this.button.textContent = "Quick add";
        this.button.disabled = false;
      }
    }
  }
}

customElements.define("quick-add-beromaster", QuickAddBeromaster);

document.addEventListener("DOMContentLoaded", () => {
  const observer = new MutationObserver(() => {
    const button = document.querySelector("#rivo-earning-rule-307688");
    if (button) {
      button.addEventListener("click", () => {
        window.location.href = "https://junip.co/forms/bero";
      });
      observer.disconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

/*
Product Item
*/

class ProductItem extends HTMLElement {
  connectedCallback() {
    if (this.dataset.variantInit === "true") return;

    this.card = this.querySelector("[data-product-card]");
    this.picker = this.querySelector("[data-variant-picker]");
    this.idInput = this.querySelector("[data-variant-id-input]");
    this.atcBtn = this.querySelector("[data-atc-btn]");
    this.atcLabel = this.querySelector("[data-atc-label]");
    this.priceEl = this.querySelector("[data-price]");
    this.compareEl = this.querySelector("[data-compare-price]");

    if (!this.picker || !this.idInput) {
      this.dataset.variantInit = "true";
      return;
    }

    this.btns = Array.from(this.picker.querySelectorAll("[data-variant-btn]"));
    if (!this.btns.length) {
      this.dataset.variantInit = "true";
      return;
    }

    this._applyPickerVisibility = () => {
      const isDesktop = window.innerWidth >= 1024;
      this.picker.style.display = isDesktop ? "flex" : "none";
    };

    this._applyAtcLabel = (available) => {
      if (!this.atcLabel) return;

      if (!available) {
        this.atcLabel.textContent = "Sold Out";
        return;
      }

      const isDesktop = window.innerWidth >= 1024;
      const sellingPlanInput = this.querySelector("[data-selling-plan-id-input]");

      if (sellingPlanInput) {
        this.atcLabel.textContent = isDesktop ? "Add Subscription" : "Add";
        return;
      }

      this.atcLabel.textContent = isDesktop ? "Add to Bag" : "Add";
    };

    this._applyPickerVisibility();
    this._onResize = () => {
      this._applyPickerVisibility();
      const available = this._activeAvailable ?? (this.atcBtn ? !this.atcBtn.disabled : true);
      this._applyAtcLabel(available);
      this.syncAtcAriaLabel();
    };
    window.addEventListener("resize", this._onResize);

    this.picker.style.margin = "0";
    this.picker.style.padding = "0";
    this.picker.style.gap = "0";

    this.btns.forEach((btn) => {
      btn.style.margin = "0";
      btn.style.padding = "0.5rem";
      btn.style.borderRadius = "0";
      btn.style.borderLeft = "0";
      btn.style.borderRight = "0";
      btn.style.borderBottom = "0";
      btn.style.borderTop = "1px solid transparent";
      btn.style.cursor = "pointer";
      btn.style.background = "white";

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setActive(btn, { announce: true });
      });
    });

    const initial = this.btns.find((b) => b.getAttribute("aria-pressed") === "true")
      || this.btns.find((b) => b.dataset.variantAvailable === "true")
      || this.btns[0];

    if (initial) this.setActive(initial);

    this.dataset.variantInit = "true";
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener("resize", this._onResize);
  }

  setActive(btn, { announce = false } = {}) {
    this.btns.forEach((b) => {
      b.setAttribute("aria-pressed", "false");
      b.style.borderTopColor = "transparent";
    });

    btn.setAttribute("aria-pressed", "true");
    btn.style.borderTopColor = "#000";
    this._activeBtn = btn;

    const variantId = btn.dataset.variantId;
    if (variantId) this.idInput.value = variantId;

    const available = btn.dataset.variantAvailable === "true";
    this._activeAvailable = available;

    if (this.atcBtn) {
      this.atcBtn.disabled = !available;
      // Adding opens the cart dialog; a sold-out button opens nothing.
      if (available) this.atcBtn.setAttribute("aria-haspopup", "dialog");
      else this.atcBtn.removeAttribute("aria-haspopup");
      this.atcBtn.classList.toggle("button--primary-gold", available);
      this.atcBtn.classList.toggle("button--sold-out", !available);
    }
    if (this._applyAtcLabel) this._applyAtcLabel(available);

    const wrap = this.querySelector("[data-price-wrap]");
    const compareEl = wrap?.querySelector("[data-price-compare]");
    const priceEl = wrap?.querySelector("[data-price-active]");

    if (priceEl) {
      priceEl.dataset.priceNonmember = btn.dataset.priceNonmember || "0";
      priceEl.dataset.priceMember = btn.dataset.priceMember || btn.dataset.priceNonmember || "0";
    }

    if (compareEl) {
      compareEl.dataset.compareNonmember = btn.dataset.compareNonmember || "0";
      compareEl.dataset.compareMember = btn.dataset.compareMember || btn.dataset.priceNonmember || "0";
    }

    applyPricingState();

    const showDiscount = window?.Bero?.hasBeromasterInCart || window?.Bero?.isRivoSubscriber;

    if (this.priceEl && btn.dataset.variantPrice) {
      const nextBase = btn.dataset.variantPrice;
      if (this.priceEl.tagName === "PRICE-DISPLAY") {
        this.priceEl.dataset.priceBase = nextBase;
        this.priceEl.basePrice = Number(nextBase) || 0;
        this.priceEl.showDiscount = showDiscount;
        if (typeof this.priceEl.renderDiscount === "function") this.priceEl.renderDiscount();
      } else {
        this.priceEl.textContent = nextBase;
      }
    }

    if (this.compareEl) {
      const compare = (btn.dataset.variantCompare || "").trim();
      if (compare) {
        if (this.compareEl.tagName === "PRICE-DISPLAY") {
          this.compareEl.dataset.priceBase = compare;
          this.compareEl.basePrice = Number(compare) || 0;
          this.compareEl.showDiscount = showDiscount;
          if (typeof this.compareEl.renderDiscount === "function") this.compareEl.renderDiscount();
          this.compareEl.style.display = "inline";
        } else {
          this.compareEl.textContent = compare;
          this.compareEl.style.display = "inline";
        }
      } else {
        if (this.compareEl.tagName === "PRICE-DISPLAY") {
          this.compareEl.textContent = "";
          this.compareEl.style.display = "none";
        } else {
          this.compareEl.textContent = "";
          this.compareEl.style.display = "none";
        }
      }
    }

    this.syncAtcAriaLabel();
    if (announce) this._announceVariant(btn, available, priceEl);
  }

  // VoiceOver/WebKit caches the button's accessible name and doesn't reliably pick up
  // price text swapped in by JS, so keep an explicit aria-label in sync with the visible text.
  // The card is aria-live="off" so this isn't read out inside carousels (Swiper live region).
  syncAtcAriaLabel() {
    if (!this.atcBtn || !this._activeBtn) return;

    const label = (this.atcLabel?.textContent || "").replace(/\s+/g, " ").trim();
    const title = this._activeBtn.dataset.variantTitle || "";
    const wrap = this.atcBtn.querySelector("[data-price-wrap]");
    const price = wrap?.querySelector("[data-price-active]")?.textContent.trim();
    const compareEl = wrap?.querySelector("[data-price-compare]");
    const compare = compareEl && !compareEl.classList.contains("hidden") ? compareEl.textContent.trim() : "";

    const parts = [title ? `${label}: ${title}` : label];
    if (this._activeAvailable !== false && price) {
      parts.push(compare ? `${price}, regular price ${compare}` : price);
    }
    if (this._activeAvailable !== false) parts.push("opens your bag");

    const next = parts.join(", ");
    if (this.atcBtn.getAttribute("aria-label") !== next) this.atcBtn.setAttribute("aria-label", next);
  }

  // Screen readers don't see the price/ATC text change, so voice it via the live region.
  _announceVariant(btn, available, priceEl) {
    const status = this.querySelector("[data-variant-status]");
    if (!status) return;

    const title = btn.dataset.variantTitle || btn.textContent.trim();
    const price = priceEl?.textContent.trim();
    const detail = available ? price : "sold out";

    status.textContent = detail ? `${title} selected, ${detail}` : `${title} selected`;
  }
}

customElements.define("product-item", ProductItem);

class AliaDiscountCode extends HTMLElement {
  connectedCallback() {
    console.log('### hello alia')
    const params = new URLSearchParams(window.location.search);
    const code = params.get("alia_discount_code");
    if (code && !sessionStorage.getItem("alia_discount_code")) {
      sessionStorage.setItem("alia_discount_code", code);
      this._triggerGwp();
    }

    // Intercept any sessionStorage.setItem for alia_discount_code to trigger GWP
    const origSetItem = sessionStorage.setItem.bind(sessionStorage);
    sessionStorage.setItem = (key, value) => {
      origSetItem(key, value);
      if (key === "alia_discount_code") {
        this._triggerGwp();
      }
    };

    document.addEventListener("click", this.handleCheckoutClick.bind(this));
    document.addEventListener("submit", this.handleCheckoutSubmit.bind(this));
  }

  _triggerGwp() {
    const cart = (typeof SiteCart !== "undefined" && SiteCart._primaryInstance) || document.querySelector("site-cart");
    if (cart) cart._addMatchingGwpGifts();
  }

  handleCheckoutClick(e) {
    const link = e.target.closest('a[href="/checkout"]');
    if (!link) return;
    const code = sessionStorage.getItem("alia_discount_code");
    if (!code) return;
    e.preventDefault();
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
  }

  handleCheckoutSubmit(e) {
    const submitter = e.submitter;
    if (!submitter || submitter.getAttribute("name") !== "checkout") return;
    const code = sessionStorage.getItem("alia_discount_code");
    if (!code) return;
    e.preventDefault();
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
  }
}

customElements.define("alia-discount-code", AliaDiscountCode);

class GeolocationManager extends HTMLElement {
  connectedCallback() {
    if (sessionStorage.getItem("bero_geo_redirected") === "true") {
      sessionStorage.removeItem("bero_geo_redirected");
      requestAnimationFrame(() => this.showModal());
      return;
    }

    if (sessionStorage.getItem("bero_geo_checked")) return;
    if (window.Bero?.currentCountryCode === "GB") return;
    if (!window.Bero?.enableGeoRedirect) return;

    this.checkGeolocation();
  }

  async checkGeolocation() {
    try {
      const res = await fetch(window.Bero.geoEndpoint);
      const data = await res.json();
      sessionStorage.setItem("bero_geo_checked", "true");

      if (data.countryCode === "GB") {
        sessionStorage.setItem("bero_geo_redirected", "true");
        this.switchToUK();
      }
    } catch (e) {
      console.warn("Geolocation check failed:", e);
    }
  }

  switchToUK() {
    const form = document.querySelector(
      "[data-header-locale-dropdown] form"
    );
    if (!form) return;

    const input = form.querySelector('input[name="country_code"]');
    if (!input) return;
    input.value = "GB";

    const siteHeader = document.querySelector("site-header");
    if (siteHeader?.setReturnToForCountry) {
      siteHeader.setReturnToForCountry(form, "GB");
    }

    form.submit();
  }

  showModal() {
    const el = document.getElementById("geo-redirect-modal");
    if (!el) return;

    const modal = el.querySelector(".modal");
    if (!modal) return;

    document.body.classList.add("overflow-hidden");
    modal.classList.add("active");

    if (window.BeroA11y) {
      this._returnFocusTo = window.BeroA11y.saveFocus();
      window.BeroA11y.setBackgroundInert(el);
      window.BeroA11y.trapFocus(modal, modal.querySelector('[role="dialog"]'));
    }

    const closeModal = () => {
      document.body.classList.remove("overflow-hidden");
      modal.classList.remove("active");
      if (window.BeroA11y) {
        window.BeroA11y.releaseFocus(modal);
        window.BeroA11y.releaseBackgroundInert();
        window.BeroA11y.restoreFocus(this._returnFocusTo);
        this._returnFocusTo = null;
      }
      document.removeEventListener("keydown", onKeydown);
    };

    const onKeydown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKeydown);

    el.querySelectorAll(".modal-close").forEach((btn) => {
      btn.addEventListener("click", closeModal);
    });
  }
}

customElements.define("geolocation-manager", GeolocationManager);
